import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  CalendarPlus,
  ChevronDown,
  CreditCard,
  Download,
  Edit2,
  Landmark,
  MailPlus,
  Paperclip,
  Pencil,
  Plus,
  Receipt,
  Save,
  Search,
  Send,
  Settings,
  Trash2,
  UploadCloud,
  X,
} from "lucide-react";
import { TailwindDateField } from "../../../components/forms/TailwindDateField";
import {
  useDomainRouterQuery,
  useDomainRouterMutation,
  useLazyDomainRouterQuery,
} from "../../../state/domainRouterHooks";
import {
  InternalNotesComposerCard,
  InternalNotesListSection,
  type InternalNoteTag,
  normalizeInternalNoteTag,
} from "../../shared/internal-notes/InternalNotesShared";
import { AppDialogFrame } from "../../../components/overlays/AppDialogFrame";
import { DetailSectionHeader } from "../../../components/navigation/DetailSectionHeader";
import type { ModuleSectionNavItem } from "../../../components/navigation/ModuleSectionNav";
import { StudentsFamiliesStatusBadge } from "../components/StudentsFamiliesStatusBadge";
import {
  ActionIconButton,
  InlineSelectControl,
} from "../components/ToolbarPrimitives";
import { useSurfaceWidthMode } from "../components/useSurfaceWidthMode";
import { isDevRuntimeBuild } from "../../../config/devRuntimeFlags";
import {
  useStudentsFamiliesFamilyCommunicationsQuery,
  useStudentsFamiliesFamilyCoreQuery,
} from "../objectTree";
import { SupportErrorMessage } from "../../../components/overlays/EngineeringSupportBanner";

type DetailTabId = "children" | "family" | "tuition" | "documents" | "notes";

type JsonRecord = Record<string, unknown>;

const joinClassNames = (...values: Array<string | false | null | undefined>) =>
  values.filter(Boolean).join(" ");

type FamilyDraft = {
  familyName: string;
  homePhone: string;
  addressStreet: string;
  addressCity: string;
  addressState: string;
  addressZip: string;
  synagogue: string;
  rabbi: string;
  hebrewFamilyName: string;
  maritalStatus: string;
  adoptedChild: string;
  stepParent: string;
  siblings: Array<{ name: string; age: string; school: string }>;
};

type StudentDraft = {
  studentId: string;
  firstName: string;
  lastName: string;
  preferredName: string;
  grade: string;
  gradeApplyingFor: string;
  applyingForYear: string;
  division: string;
  status: string;
  placeOfBirth: string;
  homeLanguage: string;
  currentSchool: string;
  principalName: string;
  principalPhone: string;
  pediatrician: string;
  pediatricianPhone: string;
  dentist: string;
  dentistPhone: string;
  caseOwnerName: string;
};

type ScheduleEventDraft = {
  id?: string;
  title: string;
  date: string;
  details: string;
};

type ContactDraft = {
  subject: string;
  message: string;
};

type NoteAttachmentDraft = {
  id: string;
  file: File;
};

type PaymentMethodDraft = {
  methodType: "card" | "bank_account";
  label: string;
  provider: string;
  lastFour: string;
  preferred: boolean;
};

type DocumentModalState = {
  open: boolean;
  mode: "create" | "edit";
  document: JsonRecord | null;
};

type DocumentFormSubmission = {
  documentId: string | null;
  name: string;
  category: string;
  status: string;
  notes: string;
  file: File | null;
  selectedStudentIds: string[];
  documentGroupId: string | null;
};

const DETAIL_TABS: Array<{ id: DetailTabId; label: string }> = [
  { id: "children", label: "Children" },
  { id: "family", label: "Family" },
  { id: "tuition", label: "Tuition" },
  { id: "documents", label: "Documents" },
  { id: "notes", label: "Notes" },
];

const STATUS_OPTIONS = [
  "Applied",
  "In-Progress",
  "Scheduled",
  "Decision",
  "Enrolled",
  "Waitlisted",
  "Archived",
];

const CASE_OWNER_OPTIONS = [
  "Admissions Desk",
  "Case Manager",
  "Rabbi Liaison",
  "Operations Team",
  "Financial Office",
];

const DOCUMENT_CATEGORY_OPTIONS = [
  "health_safety",
  "legal",
  "academic",
  "academic_support",
  "other",
];
const DOCUMENT_STATUS_OPTIONS = [
  "verified",
  "pending",
  "under_review",
  "missing_signature",
];

const ACCEPTED_UPLOAD_MIME_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/jpeg",
  "image/png",
];

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
const STUDENTS_FAMILY_DETAIL_COMPACT_WIDTH_PX = 760;

const asRecord = (value: unknown): JsonRecord =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as JsonRecord)
    : {};

const asArray = (value: unknown): JsonRecord[] =>
  Array.isArray(value) ? (value as JsonRecord[]) : [];

const readText = (value: unknown, fallback = ""): string => {
  const normalized = String(value ?? "").trim();
  return normalized || fallback;
};

const formatGradeForDisplay = (value: unknown): string => {
  const normalized = readText(value, "").trim();
  const match = normalized.match(/^grade[_\-\s]*(\d+(?:\.\d+)?)$/i);
  return match ? `Grade ${match[1]}` : normalized;
};

const normalizeGradeForStorage = (value: unknown): string => {
  const normalized = readText(value, "").trim();
  if (!normalized) return "";

  const gradeLikeMatch = normalized.match(/^grade[_\-\s]*(\d+(?:\.\d+)?)$/i);
  if (gradeLikeMatch) {
    return `grade_${gradeLikeMatch[1]}`;
  }

  const plainGradeMatch = normalized.match(/^grade\s+(\d+(?:\.\d+)?)$/i);
  if (plainGradeMatch) {
    return `grade_${plainGradeMatch[1]}`;
  }

  if (/^\d+(?:\.\d+)?$/.test(normalized)) {
    return `grade_${normalized}`;
  }

  return normalized;
};

const readRouterErrorMessage = (error: unknown): string => {
  if (!error) return "";
  if (typeof error === "string") return error.trim();
  if (error instanceof Error) return error.message.trim();

  if (typeof error === "object") {
    const candidate = error as {
      message?: unknown;
      error?: unknown;
      data?: { message?: unknown } | unknown;
    };

    const topLevel = readText(candidate.message, "");
    if (topLevel) return topLevel;

    const nestedError = readText(
      (candidate.error as { message?: unknown } | undefined)?.message,
      "",
    );
    if (nestedError) return nestedError;

    const nestedData = readText(
      (candidate.data as { message?: unknown } | undefined)?.message,
      "",
    );
    if (nestedData) return nestedData;

    return JSON.stringify(error);
  }

  return String(error);
};

const readNumber = (value: unknown, fallback = 0): number => {
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
};

const formatCurrency = (value: unknown): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(readNumber(value, 0));

const normalizeTab = (value: string | undefined): DetailTabId | null => {
  if (!value) return null;
  return (DETAIL_TABS.find((tab) => tab.id === value)?.id ??
    null) as DetailTabId | null;
};

const inferTabFromPath = (pathname: string): DetailTabId | null => {
  const normalized = pathname.replace(/\/+$/, "");
  const match = DETAIL_TABS.find((tab) => normalized.endsWith(`/${tab.id}`));
  return match?.id ?? null;
};

const buildFamilyDetailPath = (familyId: string, tab: DetailTabId): string =>
  `/school/students/family/${familyId}/${tab}`;

const createClientId = (): string => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const resolveAcademicYear = (): string => {
  const now = new Date();
  const startYear =
    now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  return `${startYear}-${startYear + 1}`;
};

const truncateAttachmentLabel = (label: string): string =>
  label.length > 25 ? `${label.slice(0, 25)}...` : label;

const normalizeWorkflowStatus = (value: string): string =>
  value.trim() || "In-Progress";

const validateUploadFile = (file: File): string | null => {
  if (!ACCEPTED_UPLOAD_MIME_TYPES.includes(file.type)) {
    return "Accepted files: PDF, DOCX, JPEG, PNG.";
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return "File must be 10MB or smaller.";
  }
  return null;
};

const uploadFileToSignedUrl = async (
  uploadUrl: string,
  file: File,
): Promise<void> => {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": file.type || "application/octet-stream",
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`Upload failed with status ${response.status}`);
  }
};

const ContactFamilyModal = ({
  open,
  initialDraft,
  onClose,
  onSubmit,
  loading,
}: {
  open: boolean;
  initialDraft: ContactDraft;
  onClose: () => void;
  onSubmit: (draft: ContactDraft) => Promise<void>;
  loading: boolean;
}) => {
  const [draft, setDraft] = useState<ContactDraft>(initialDraft);

  useEffect(() => {
    if (!open) return;
    setDraft(initialDraft);
  }, [initialDraft, open]);

  if (!open) return null;

  return (
    <AppDialogFrame
      open={open}
      onClose={onClose}
      title="Contact Family"
      description="Send communication to the family record."
      testId="students-families-contact-family-modal"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl px-4 py-2 text-sm font-semibold text-irongrey/70 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSubmit(draft)}
            disabled={loading || !draft.subject.trim() || !draft.message.trim()}
            className="rounded-xl bg-primary px-4 py-2 text-sm font-bold text-white shadow-lg shadow-primary/20 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
          >
            {loading ? "Sending..." : "Send"}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <label className="block">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
            Subject
          </span>
          <input
            value={draft.subject}
            onChange={(event) =>
              setDraft((prev) => ({ ...prev, subject: event.target.value }))
            }
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-irongrey"
            placeholder="Subject line"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
            Message
          </span>
          <textarea
            value={draft.message}
            onChange={(event) =>
              setDraft((prev) => ({ ...prev, message: event.target.value }))
            }
            className="min-h-[140px] w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-irongrey"
            placeholder="Write your message"
          />
        </label>
      </div>
    </AppDialogFrame>
  );
};

const ScheduleModal = ({
  open,
  initial,
  onClose,
  onSubmit,
  loading,
}: {
  open: boolean;
  initial: ScheduleEventDraft;
  onClose: () => void;
  onSubmit: (draft: ScheduleEventDraft) => Promise<void>;
  loading: boolean;
}) => {
  const [draft, setDraft] = useState<ScheduleEventDraft>(initial);

  useEffect(() => {
    if (!open) return;
    setDraft(initial);
  }, [initial, open]);

  if (!open) return null;

  return (
    <AppDialogFrame
      open={open}
      onClose={onClose}
      title={draft.id ? "Modify Schedule Event" : "Schedule Event"}
      description="Create or update timeline scheduling entries."
      testId="students-families-schedule-modal"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl px-4 py-2 text-sm font-semibold text-irongrey/70 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSubmit(draft)}
            disabled={loading || !draft.title.trim() || !draft.date}
            className="rounded-xl bg-primary px-4 py-2 text-sm font-bold text-white shadow-lg shadow-primary/20 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
          >
            {loading ? "Saving..." : draft.id ? "Update" : "Save"}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <label className="block">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
            Title
          </span>
          <input
            value={draft.title}
            onChange={(event) =>
              setDraft((prev) => ({ ...prev, title: event.target.value }))
            }
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-irongrey"
            placeholder="Interview"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
            Date
          </span>
          <TailwindDateField
            value={draft.date}
            onChange={(event) =>
              setDraft((prev) => ({ ...prev, date: event.target.value }))
            }
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-irongrey"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
            Details
          </span>
          <textarea
            value={draft.details}
            onChange={(event) =>
              setDraft((prev) => ({ ...prev, details: event.target.value }))
            }
            className="min-h-[120px] w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-irongrey"
            placeholder="Optional details"
          />
        </label>
      </div>
    </AppDialogFrame>
  );
};

const DocumentModal = ({
  open,
  students,
  initialDocument,
  onClose,
  onSubmit,
  loading,
}: {
  open: boolean;
  students: JsonRecord[];
  initialDocument: JsonRecord | null;
  onClose: () => void;
  onSubmit: (payload: DocumentFormSubmission) => Promise<void>;
  loading: boolean;
}) => {
  const isEdit = Boolean(initialDocument);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("academic");
  const [status, setStatus] = useState("pending");
  const [notes, setNotes] = useState("");
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const currentStudentIds = students
      .map((student) => readText(student.id, ""))
      .filter(Boolean);

    if (initialDocument) {
      setName(readText(initialDocument.name, "Document"));
      setCategory(
        readText(initialDocument.category, "other")
          .toLowerCase()
          .replace(/\s+/g, "_"),
      );
      setStatus(
        readText(initialDocument.status, "pending")
          .toLowerCase()
          .replace(/\s+/g, "_"),
      );
      setNotes(readText(initialDocument.notes, ""));
      const initialStudentId = readText(initialDocument.studentId, "");
      setSelectedStudentIds(
        initialStudentId ? [initialStudentId] : currentStudentIds.slice(0, 1),
      );
      setFile(null);
      setError(null);
      return;
    }

    setName("");
    setCategory("academic");
    setStatus("pending");
    setNotes("");
    setSelectedStudentIds(currentStudentIds);
    setFile(null);
    setError(null);
  }, [open, initialDocument, students]);

  if (!open) return null;

  const toggleStudent = (studentId: string) => {
    setSelectedStudentIds((prev) =>
      prev.includes(studentId)
        ? prev.filter((value) => value !== studentId)
        : [...prev, studentId],
    );
  };

  const onPickFile = (incoming: File | null) => {
    if (!incoming) return;
    const validationError = validateUploadFile(incoming);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setFile(incoming);
    if (!name.trim()) {
      setName(incoming.name);
    }
  };

  const submitDisabled =
    loading ||
    !name.trim() ||
    (!isEdit && !file) ||
    selectedStudentIds.length === 0;

  return (
    <AppDialogFrame
      open={open}
      onClose={onClose}
      title={isEdit ? "Modify Document" : "Add Document"}
      description="Upload and persist family document records."
      testId="students-families-documents-modal"
      maxWidthClassName="max-w-2xl"
      bodyClassName="space-y-5"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl px-4 py-2 text-sm font-semibold text-irongrey/70 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={submitDisabled}
            onClick={() =>
              onSubmit({
                documentId: readText(initialDocument?.id, "") || null,
                name,
                category,
                status,
                notes,
                file,
                selectedStudentIds,
                documentGroupId:
                  readText(initialDocument?.documentGroupId, "") || null,
              })
            }
            className="rounded-xl bg-primary px-4 py-2 text-sm font-bold text-white shadow-lg shadow-primary/20 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
          >
            {loading
              ? "Saving..."
              : isEdit
                ? "Update Document"
                : "Add Document"}
          </button>
        </>
      }
    >
      <div className="space-y-5">
        <div>
          <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
            Apply to Students
          </p>
          <div className="flex flex-wrap gap-2">
            {students.map((student) => {
              const studentId = readText(student.id, "");
              const label =
                `${readText(student.firstName, "")} ${readText(student.lastName, "")}`.trim() ||
                "Student";
              const active = selectedStudentIds.includes(studentId);
              return (
                <button
                  key={studentId}
                  type="button"
                  onClick={() => toggleStudent(studentId)}
                  className={`rounded-xl border px-4 py-2 text-xs font-bold transition ${active ? "border-primary bg-primary/10 text-primary" : "border-gray-100 bg-white text-irongrey/60 hover:border-gray-300"}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
              Category
            </span>
            <div className="relative">
              <select
                value={category}
                onChange={(event) => setCategory(event.target.value)}
                className="w-full appearance-none rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-irongrey"
              >
                {DOCUMENT_CATEGORY_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={14}
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-irongrey/30"
              />
            </div>
          </label>

          <label className="block">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
              Status
            </span>
            <div className="relative">
              <select
                value={status}
                onChange={(event) => setStatus(event.target.value)}
                className="w-full appearance-none rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-irongrey"
              >
                {DOCUMENT_STATUS_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={14}
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-irongrey/30"
              />
            </div>
          </label>
        </div>

        <label className="block">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
            Document Name
          </span>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-irongrey"
            placeholder="Report Card Q1"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
            Notes
          </span>
          <textarea
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            className="min-h-[84px] w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-irongrey"
            placeholder="Optional verification note"
          />
        </label>

        <div>
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
            File Attachment
          </span>
          <input
            ref={fileInputRef}
            type="file"
            data-testid="students-families-documents-upload-input"
            className="hidden"
            onChange={(event) => onPickFile(event.target.files?.[0] ?? null)}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex w-full items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 px-6 py-8 text-sm font-semibold text-irongrey/60 hover:border-primary/30 hover:bg-primary/5"
          >
            <UploadCloud size={20} className="text-primary" />
            {file
              ? file.name
              : isEdit
                ? "Select replacement file (optional)"
                : "Select file to upload"}
          </button>
          {error ? (
            <p className="mt-2 text-xs font-semibold text-rose-600">{error}</p>
          ) : null}
        </div>
      </div>
    </AppDialogFrame>
  );
};

const PaymentMethodModal = ({
  open,
  onClose,
  onSubmit,
  loading,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (draft: PaymentMethodDraft) => Promise<void>;
  loading: boolean;
}) => {
  const [draft, setDraft] = useState<PaymentMethodDraft>({
    methodType: "card",
    label: "",
    provider: "",
    lastFour: "",
    preferred: false,
  });

  useEffect(() => {
    if (!open) return;
    setDraft({
      methodType: "card",
      label: "",
      provider: "",
      lastFour: "",
      preferred: false,
    });
  }, [open]);

  if (!open) return null;

  return (
    <AppDialogFrame
      open={open}
      onClose={onClose}
      title="Add Payment Method"
      description="Persist payment method under the family billing profile."
      testId="students-families-payment-method-modal"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl px-4 py-2 text-sm font-semibold text-irongrey/70 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={loading || !draft.label.trim()}
            onClick={() => onSubmit(draft)}
            className="rounded-xl bg-primary px-4 py-2 text-sm font-bold text-white shadow-lg shadow-primary/20 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
          >
            {loading ? "Saving..." : "Add"}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
              Method Type
            </span>
            <select
              value={draft.methodType}
              onChange={(event) =>
                setDraft((prev) => ({
                  ...prev,
                  methodType: event.target
                    .value as PaymentMethodDraft["methodType"],
                }))
              }
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-irongrey"
            >
              <option value="card">Card</option>
              <option value="bank_account">Bank Account</option>
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
              Label
            </span>
            <input
              value={draft.label}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, label: event.target.value }))
              }
              placeholder="Visa •••• 4242"
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-irongrey"
            />
          </label>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
              Provider
            </span>
            <input
              value={draft.provider}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, provider: event.target.value }))
              }
              placeholder="Chase"
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-irongrey"
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
              Last Four
            </span>
            <input
              value={draft.lastFour}
              onChange={(event) =>
                setDraft((prev) => ({
                  ...prev,
                  lastFour: event.target.value
                    .replace(/[^0-9]/g, "")
                    .slice(0, 4),
                }))
              }
              placeholder="4242"
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-irongrey"
            />
          </label>
        </div>

        <label className="inline-flex items-center gap-2 text-sm font-semibold text-irongrey">
          <input
            type="checkbox"
            checked={draft.preferred}
            onChange={(event) =>
              setDraft((prev) => ({ ...prev, preferred: event.target.checked }))
            }
            className="h-4 w-4 rounded border-gray-300 text-primary accent-primary focus:ring-primary focus:ring-offset-0 focus:outline-none cursor-pointer"
          />
          Set as preferred payment source
        </label>
      </div>
    </AppDialogFrame>
  );
};

const buildStudentDrafts = (
  students: JsonRecord[],
): Record<string, StudentDraft> =>
  students.reduce<Record<string, StudentDraft>>((acc, student) => {
    const studentId = readText(student.id, "");
    if (!studentId) return acc;
    acc[studentId] = {
      studentId,
      firstName: readText(student.firstName),
      lastName: readText(student.lastName),
      preferredName: readText(student.preferredName),
      grade: readText(student.grade),
      gradeApplyingFor: readText(student.gradeApplyingFor),
      applyingForYear: readText(student.applyingForYear),
      division: readText(student.division),
      status: readText(student.status, "active"),
      placeOfBirth: readText(student.placeOfBirth),
      homeLanguage: readText(student.homeLanguage),
      currentSchool: readText(student.currentSchool),
      principalName: readText(student.principalName),
      principalPhone: readText(student.principalPhone),
      pediatrician: readText(student.pediatrician),
      pediatricianPhone: readText(student.pediatricianPhone),
      dentist: readText(student.dentist),
      dentistPhone: readText(student.dentistPhone),
      caseOwnerName: readText(student.caseOwnerName),
    };
    return acc;
  }, {});

const serializeStudentDrafts = (
  drafts: Record<string, StudentDraft>,
): string =>
  JSON.stringify(
    Object.keys(drafts)
      .sort()
      .map((studentId) => [studentId, drafts[studentId]]),
  );

const buildFamilyDraft = (family: JsonRecord): FamilyDraft => {
  const address = asRecord(family.address);
  const siblings = parseSiblings(asArray(family.siblings));
  return {
    familyName: readText(family.familyName, "Family"),
    homePhone: readText(family.homePhone),
    addressStreet: readText(address.street),
    addressCity: readText(address.city),
    addressState: readText(address.state),
    addressZip: readText(address.zipCode),
    synagogue: readText(family.synagogue),
    rabbi: readText(family.rabbi),
    hebrewFamilyName: readText(family.hebrewFamilyName),
    maritalStatus: readText(family.maritalStatus),
    adoptedChild: readText(family.adoptedChild),
    stepParent: readText(family.stepParent),
    siblings,
  };
};

const serializeFamilyDraft = (draft: FamilyDraft): string =>
  JSON.stringify({
    ...draft,
    siblings: normalizeSiblingDrafts(draft.siblings),
  });

export const areFamilyDraftsEquivalent = (
  left: FamilyDraft,
  right: FamilyDraft,
): boolean => serializeFamilyDraft(left) === serializeFamilyDraft(right);

export const areStudentDraftMapsEquivalent = (
  left: Record<string, StudentDraft>,
  right: Record<string, StudentDraft>,
): boolean => serializeStudentDrafts(left) === serializeStudentDrafts(right);

export const getStudentsFamiliesPrimaryAction = (
  tab: DetailTabId,
  isEditing: boolean,
): "edit" | "save" | null => {
  if (tab !== "children" && tab !== "family") {
    return null;
  }
  return isEditing ? "save" : "edit";
};

function parseSiblings(
  items: JsonRecord[],
): Array<{ name: string; age: string; school: string }> {
  return items.map((item) => ({
    name: readText(item.name),
    age: readText(item.age),
    school: readText(item.school),
  }));
}

const normalizeStatusLabel = (value: string): string =>
  value.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

type TuitionTransactionKind = "CHARGE" | "PAYMENT" | "ADJUSTMENT";

const classifyTuitionTransaction = (
  entryType: string,
): TuitionTransactionKind => {
  const normalized = entryType.trim().toLowerCase();
  if (normalized === "payment" || normalized === "refund") {
    return "PAYMENT";
  }
  if (
    normalized === "credit" ||
    normalized === "scholarship" ||
    normalized === "adjustment"
  ) {
    return "ADJUSTMENT";
  }
  return "CHARGE";
};

const formatGradeLabel = (grade: string): string =>
  grade.trim().replace(/_/g, " ").toUpperCase();

const normalizeNullableDraftValue = (value: unknown): string | null => {
  const normalized = readText(value, "").trim();
  return normalized ? normalized : null;
};

const normalizeRequiredDraftValue = (
  nextValue: unknown,
  previousValue: unknown,
): string => {
  const next = readText(nextValue, "").trim();
  if (next) return next;
  return readText(previousValue, "").trim();
};

const normalizeSiblingDrafts = (
  siblings: Array<{ name: string; age: string; school: string }>,
) =>
  siblings.map((sibling) => ({
    name: readText(sibling.name, "").trim(),
    age: readText(sibling.age, "").trim(),
    school: readText(sibling.school, "").trim(),
  }));

const isMissingOperationError = (error: unknown): boolean => {
  if (!error) return false;
  const message = JSON.stringify(error);
  return /FieldUndefined|Cannot query field|is undefined|Unknown operation|Unknown field|Validation error of type|getFamilyBalance|family balance|balance summary/i.test(
    message,
  );
};

const isActionableRouterError = (error: unknown): boolean =>
  Boolean(error) && !isMissingOperationError(error);

const formatTimelineDate = (value: unknown): string => {
  const source = readText(value, "");
  if (!source) return "Today";
  const parsed = new Date(source);
  if (Number.isNaN(parsed.getTime())) return source;
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
};

const formatTimelineTime = (value: unknown): string => {
  const source = readText(value, "");
  if (!source) return "";
  const parsed = new Date(source);
  if (Number.isNaN(parsed.getTime())) return source;
  return parsed.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const buildFamilyTransactionsQueryArgs = (options: {
  familyId: string;
  academicYear: string;
  nextToken?: string;
}) => ({
  familyId: options.familyId,
  academicYear: options.academicYear,
  limit: 20,
  nextToken: options.nextToken,
});

export const mergeFamilyTransactionPage = (options: {
  familyId: string;
  cursor?: string;
  payload: unknown;
  previousItems: JsonRecord[];
}) => {
  const page = asRecord(options.payload);
  const pageItems = asArray(page.items);
  const nextToken = readText(page.nextToken, "") || null;

  return {
    items: options.cursor ? [...options.previousItems, ...pageItems] : pageItems,
    nextToken,
    pageKey:
      `${options.familyId}:${options.cursor ?? "__first__"}:${nextToken ?? "none"}:${pageItems.length}`,
  };
};

export const FamilyDetail = ({ forcedTab }: { forcedTab?: DetailTabId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const familyId = readText(params.familyId, "");
  const tab =
    forcedTab ??
    normalizeTab(params.tab) ??
    inferTabFromPath(location.pathname) ??
    "children";
  const academicYear = useMemo(resolveAcademicYear, []);
  const wantsTuitionData = tab === "tuition";
  const wantsDocumentsData = tab === "documents";
  const wantsNotesData = tab === "notes";

  const familyQuery = useStudentsFamiliesFamilyCoreQuery();
  const communicationsQuery = useStudentsFamiliesFamilyCommunicationsQuery();
  const balanceQuery = useDomainRouterQuery(
    "getFamilyBalance",
    { familyId, academicYear },
    { skip: !familyId || !wantsTuitionData },
  );
  const documentsQuery = useDomainRouterQuery(
    "listFamilyDocuments",
    { familyId, limit: 200 },
    { skip: !familyId || !wantsDocumentsData },
  );
  const paymentMethodsQuery = useDomainRouterQuery(
    "listFamilyPaymentMethods",
    { familyId, limit: 100 },
    { skip: !familyId || !wantsTuitionData },
  );

  const [transactionCursorArg, setTransactionCursorArg] = useState<
    string | undefined
  >(undefined);
  const transactionsQuery = useDomainRouterQuery(
    "listFamilyAccountTransactions",
    buildFamilyTransactionsQueryArgs({
      familyId,
      academicYear,
      nextToken: transactionCursorArg,
    }),
    { skip: !familyId || !wantsTuitionData },
  );
  const timelineQuery = useDomainRouterQuery(
    "listFamilyTimelineEvents",
    { familyId, limit: 100 },
    { skip: !familyId || !wantsNotesData },
  );

  const [transactions, setTransactions] = useState<JsonRecord[]>([]);
  const [transactionsNextToken, setTransactionsNextToken] = useState<
    string | null
  >(null);
  const transactionsPageKeyRef = useRef<string>("");
  const detailRootRef = useRef<HTMLDivElement | null>(null);
  const detailSurface = useSurfaceWidthMode(detailRootRef, {
    stackedBreakpointPx: STUDENTS_FAMILY_DETAIL_COMPACT_WIDTH_PX,
    compactBreakpointPx: STUDENTS_FAMILY_DETAIL_COMPACT_WIDTH_PX,
    measureRelativeToParent: true,
  });

  const [statusValue, setStatusValue] = useState("In-Progress");
  const [isEditing, setIsEditing] = useState(false);
  const [familyDraft, setFamilyDraft] = useState<FamilyDraft>({
    familyName: "Family",
    homePhone: "",
    addressStreet: "",
    addressCity: "",
    addressState: "",
    addressZip: "",
    synagogue: "",
    rabbi: "",
    hebrewFamilyName: "",
    maritalStatus: "",
    adoptedChild: "",
    stepParent: "",
    siblings: [],
  });
  const [studentDrafts, setStudentDrafts] = useState<
    Record<string, StudentDraft>
  >({});

  const [contactOpen, setContactOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleDraft, setScheduleDraft] = useState<ScheduleEventDraft>({
    title: "Interview",
    date: "",
    details: "",
  });

  const [noteText, setNoteText] = useState("");
  const [noteTag, setNoteTag] = useState<InternalNoteTag>("general");
  const [noteAttachments, setNoteAttachments] = useState<NoteAttachmentDraft[]>(
    [],
  );
  const noteAttachmentInputRef = useRef<HTMLInputElement | null>(null);

  const [documentModal, setDocumentModal] = useState<DocumentModalState>({
    open: false,
    mode: "create",
    document: null,
  });
  const [documentsSearch, setDocumentsSearch] = useState("");

  const [paymentMethodOpen, setPaymentMethodOpen] = useState(false);
  const [transactionFilter, setTransactionFilter] = useState<
    "all" | "charges" | "payments" | "adjustments"
  >("all");

  const [updateFamilyWorkflowStatus, updateFamilyWorkflowStatusState] =
    useDomainRouterMutation("updateFamilyWorkflowStatus");
  const [sendFamilyCommunication, sendFamilyCommunicationState] =
    useDomainRouterMutation("sendFamilyCommunication");
  const [createFamilyScheduleEvent, createFamilyScheduleEventState] =
    useDomainRouterMutation("createFamilyScheduleEvent");
  const [updateFamilyScheduleEvent, updateFamilyScheduleEventState] =
    useDomainRouterMutation("updateFamilyScheduleEvent");
  const [createFamilyNote, createFamilyNoteState] =
    useDomainRouterMutation("createFamilyNote");
  const [deleteFamilyNote, deleteFamilyNoteState] =
    useDomainRouterMutation("deleteFamilyNote");
  const [updateStudentProfile, updateStudentProfileState] =
    useDomainRouterMutation("updateStudentProfile");
  const [assignStudentCaseOwner, assignStudentCaseOwnerState] =
    useDomainRouterMutation("assignStudentCaseOwner");
  const [updateFamilyProfile, updateFamilyProfileState] =
    useDomainRouterMutation("updateFamilyProfile");
  const [requestFamilyDocumentUpload, requestFamilyDocumentUploadState] =
    useDomainRouterMutation("requestFamilyDocumentUpload");
  const [createFamilyDocumentRecord, createFamilyDocumentRecordState] =
    useDomainRouterMutation("createFamilyDocumentRecord");
  const [updateFamilyDocumentRecord, updateFamilyDocumentRecordState] =
    useDomainRouterMutation("updateFamilyDocumentRecord");
  const [deleteFamilyDocumentRecord, deleteFamilyDocumentRecordState] =
    useDomainRouterMutation("deleteFamilyDocumentRecord");
  const [
    requestFamilyNoteAttachmentUpload,
    requestFamilyNoteAttachmentUploadState,
  ] = useDomainRouterMutation("requestFamilyNoteAttachmentUpload");
  const [createFamilyPaymentMethod, createFamilyPaymentMethodState] =
    useDomainRouterMutation("createFamilyPaymentMethod");
  const [updateFamilyPaymentMethod, updateFamilyPaymentMethodState] =
    useDomainRouterMutation("updateFamilyPaymentMethod");

  const [runDownloadQuery] =
    useLazyDomainRouterQuery<"getFamilyDocumentDownloadUrl">();

  if (!familyId) {
    return <Navigate replace to="/school/students/dashboard" />;
  }

  const familyPayload = asRecord(familyQuery.data);
  const familyRecord = asRecord(familyPayload.family);
  const students = asArray(familyPayload.students);
  const parentContacts = asArray(familyPayload.parentContacts);
  const primaryContact = parentContacts[0] ?? {};

  const timelineItems = asArray(asRecord(timelineQuery.data).items);
  const communicationItems = asArray(asRecord(communicationsQuery.data).items);
  const documentItems = asArray(asRecord(documentsQuery.data).items);
  const paymentMethodItems = asArray(asRecord(paymentMethodsQuery.data).items);

  const familyStatusFromData = normalizeWorkflowStatus(
    readText(
      familyRecord.workflowStatus,
      readText(familyRecord.status, "In-Progress"),
    ),
  );
  const hydratedFamilyDraft = useMemo(
    () => buildFamilyDraft(familyRecord),
    [familyQuery.data],
  );
  const hydratedStudentDrafts = useMemo(
    () => buildStudentDrafts(students),
    [familyQuery.data],
  );
  const hydratedFamilyDraftSignature = useMemo(
    () => serializeFamilyDraft(hydratedFamilyDraft),
    [hydratedFamilyDraft],
  );
  const hydratedStudentDraftsSignature = useMemo(
    () => serializeStudentDrafts(hydratedStudentDrafts),
    [hydratedStudentDrafts],
  );

  useEffect(() => {
    setTransactionCursorArg(undefined);
    setTransactions([]);
    setTransactionsNextToken(null);
    transactionsPageKeyRef.current = "";
  }, [familyId, academicYear]);

  useEffect(() => {
    if (!transactionsQuery.data) return;
    const mergedPage = mergeFamilyTransactionPage({
      familyId,
      cursor: transactionCursorArg,
      payload: transactionsQuery.data,
      previousItems: transactions,
    });
    const pageKey = mergedPage.pageKey;
    if (transactionsPageKeyRef.current === pageKey) {
      return;
    }
    transactionsPageKeyRef.current = pageKey;
    setTransactions(mergedPage.items);
    setTransactionsNextToken(mergedPage.nextToken);
  }, [familyId, transactionCursorArg, transactions, transactionsQuery.data]);

  useEffect(() => {
    if (!familyQuery.data || isEditing) return;
    setStatusValue((current) =>
      current === familyStatusFromData ? current : familyStatusFromData,
    );
    setFamilyDraft((current) =>
      areFamilyDraftsEquivalent(current, hydratedFamilyDraft)
        ? current
        : hydratedFamilyDraft,
    );
    setStudentDrafts((current) =>
      areStudentDraftMapsEquivalent(current, hydratedStudentDrafts)
        ? current
        : hydratedStudentDrafts,
    );
  }, [
    familyQuery.data,
    hydratedFamilyDraft,
    hydratedFamilyDraftSignature,
    hydratedStudentDrafts,
    hydratedStudentDraftsSignature,
    isEditing,
    familyStatusFromData,
  ]);

  const allTimelineRows = useMemo(() => {
    const rows = [
      ...timelineItems.map((entry) => ({
        id: readText(entry.id, createClientId()),
        title: readText(
          entry.title,
          readText(entry.eventType, "Timeline Event"),
        ),
        description: readText(entry.content, "-"),
        createdAt: readText(entry.createdAt),
        eventType: readText(entry.eventType, ""),
        source: "timeline" as const,
        raw: entry,
      })),
      ...communicationItems.map((entry) => ({
        id: readText(entry.id, createClientId()),
        title: "Communication Sent",
        description: readText(entry.subject, readText(entry.body, "-")),
        createdAt: readText(entry.sentAt, readText(entry.createdAt)),
        eventType: "communication",
        source: "communication" as const,
        raw: entry,
      })),
    ];

    return rows.sort((left, right) => {
      const leftTime = Date.parse(left.createdAt || "");
      const rightTime = Date.parse(right.createdAt || "");
      if (Number.isNaN(leftTime) || Number.isNaN(rightTime)) return 0;
      return rightTime - leftTime;
    });
  }, [timelineItems, communicationItems]);

  const filteredDocuments = useMemo(() => {
    const query = documentsSearch.trim().toLowerCase();
    if (!query) return documentItems;
    return documentItems.filter(
      (doc) =>
        readText(doc.name).toLowerCase().includes(query) ||
        readText(doc.category).toLowerCase().includes(query) ||
        readText(doc.studentId).toLowerCase().includes(query),
    );
  }, [documentItems, documentsSearch]);

  const hasRouterError = Boolean(
    isActionableRouterError(familyQuery.error) ||
      (wantsTuitionData && isActionableRouterError(balanceQuery.error)) ||
      (wantsTuitionData && isActionableRouterError(transactionsQuery.error)) ||
      isActionableRouterError(updateFamilyWorkflowStatusState.error) ||
      isActionableRouterError(sendFamilyCommunicationState.error) ||
      isActionableRouterError(createFamilyScheduleEventState.error) ||
      isActionableRouterError(updateFamilyScheduleEventState.error) ||
      isActionableRouterError(createFamilyNoteState.error) ||
      isActionableRouterError(deleteFamilyNoteState.error) ||
      isActionableRouterError(updateStudentProfileState.error) ||
      isActionableRouterError(assignStudentCaseOwnerState.error) ||
      isActionableRouterError(updateFamilyProfileState.error) ||
      isActionableRouterError(requestFamilyDocumentUploadState.error) ||
      isActionableRouterError(createFamilyDocumentRecordState.error) ||
      isActionableRouterError(updateFamilyDocumentRecordState.error) ||
      isActionableRouterError(deleteFamilyDocumentRecordState.error) ||
      isActionableRouterError(requestFamilyNoteAttachmentUploadState.error) ||
      isActionableRouterError(createFamilyPaymentMethodState.error) ||
      isActionableRouterError(updateFamilyPaymentMethodState.error),
  );

  const familyDetailHydrated =
    Boolean(familyQuery.data) && !familyQuery.isLoading;

  const headerBusy =
    updateFamilyWorkflowStatusState.isLoading ||
    createFamilyScheduleEventState.isLoading ||
    updateFamilyScheduleEventState.isLoading ||
    sendFamilyCommunicationState.isLoading ||
    updateStudentProfileState.isLoading ||
    updateFamilyProfileState.isLoading;
  const isCompactLayout = detailSurface.isCompact;
  const layoutMode = detailSurface.mode;
  const shouldCollapseTabs = detailSurface.isStacked;
  const showBackText = !isCompactLayout;
  const showPrimaryActionText =
    !shouldCollapseTabs &&
    (detailSurface.width === 0 || detailSurface.width > 980);
  const sectionNavItems = useMemo<ModuleSectionNavItem[]>(
    () =>
      DETAIL_TABS.map((item) => ({
        id: item.id,
        label: item.label,
        to: buildFamilyDetailPath(familyId, item.id),
      })),
    [familyId],
  );
  const headerLeadingClass = shouldCollapseTabs
    ? "flex w-full min-w-0 flex-col items-stretch gap-3 sm:flex-row sm:items-center"
    : isCompactLayout
      ? "flex items-center gap-2 flex-1 min-w-0"
      : "flex items-center gap-4 md:gap-8 flex-1 min-w-0";
  const actionRowClass = shouldCollapseTabs
    ? "mb-0 flex w-full flex-wrap items-center justify-start gap-2 sm:justify-end"
    : "mb-[0.3rem] gap-2";
  const primaryHeaderAction = getStudentsFamiliesPrimaryAction(tab, isEditing);

  const saveMutationErrorMessage = useMemo(
    () =>
      readRouterErrorMessage(familyQuery.error) ||
      readRouterErrorMessage(balanceQuery.error) ||
      readRouterErrorMessage(transactionsQuery.error) ||
      readRouterErrorMessage(updateFamilyWorkflowStatusState.error) ||
      readRouterErrorMessage(sendFamilyCommunicationState.error) ||
      readRouterErrorMessage(createFamilyScheduleEventState.error) ||
      readRouterErrorMessage(updateFamilyScheduleEventState.error) ||
      readRouterErrorMessage(createFamilyNoteState.error) ||
      readRouterErrorMessage(deleteFamilyNoteState.error) ||
      readRouterErrorMessage(updateStudentProfileState.error) ||
      readRouterErrorMessage(assignStudentCaseOwnerState.error) ||
      readRouterErrorMessage(updateFamilyProfileState.error) ||
      readRouterErrorMessage(requestFamilyDocumentUploadState.error) ||
      readRouterErrorMessage(createFamilyDocumentRecordState.error) ||
      readRouterErrorMessage(updateFamilyDocumentRecordState.error) ||
      readRouterErrorMessage(deleteFamilyDocumentRecordState.error) ||
      readRouterErrorMessage(requestFamilyNoteAttachmentUploadState.error) ||
      readRouterErrorMessage(createFamilyPaymentMethodState.error) ||
      readRouterErrorMessage(updateFamilyPaymentMethodState.error),
    [
      familyQuery.error,
      balanceQuery.error,
      transactionsQuery.error,
      updateFamilyWorkflowStatusState.error,
      sendFamilyCommunicationState.error,
      createFamilyScheduleEventState.error,
      updateFamilyScheduleEventState.error,
      createFamilyNoteState.error,
      deleteFamilyNoteState.error,
      updateStudentProfileState.error,
      assignStudentCaseOwnerState.error,
      updateFamilyProfileState.error,
      requestFamilyDocumentUploadState.error,
      createFamilyDocumentRecordState.error,
      updateFamilyDocumentRecordState.error,
      deleteFamilyDocumentRecordState.error,
      requestFamilyNoteAttachmentUploadState.error,
      createFamilyPaymentMethodState.error,
      updateFamilyPaymentMethodState.error,
    ],
  );

  const balance = asRecord(balanceQuery.data);
  const balanceDue = readNumber(balance.netBalance, 0);

  const studentBillingRows = useMemo(() => {
    if (students.length === 0) return [];
    const studentCount = Math.max(students.length, 1);
    const fallbackTuition = readNumber(balance.totalCharges, 0) / studentCount;
    const fallbackCredits =
      Math.abs(readNumber(balance.totalCredits, 0)) / studentCount;

    return students.map((student, index) => {
      const studentId = readText(student.id, `student-${index}`);
      const firstName = readText(student.firstName, "Student");
      const lastName = readText(student.lastName);
      const displayName =
        [firstName, lastName].filter(Boolean).join(" ").trim() || "Student";
      const gradeLabel = formatGradeLabel(readText(student.grade, "Grade"));
      const studentTransactions = transactions.filter(
        (entry) => readText(asRecord(entry).studentId) === studentId,
      );

      let tuition = 0;
      let paid = 0;
      for (const entry of studentTransactions) {
        const amount = readNumber(entry.amount, 0);
        if (amount > 0) tuition += amount;
        if (amount < 0) paid += Math.abs(amount);
      }

      if (studentTransactions.length === 0) {
        tuition = fallbackTuition;
        paid = fallbackCredits;
      }

      const balanceForStudent = Math.max(tuition - paid, 0);
      const progress =
        tuition > 0 ? Math.min(100, Math.round((paid / tuition) * 100)) : 0;

      return {
        id: studentId,
        displayName,
        gradeLabel,
        balance: balanceForStudent,
        tuition,
        paid,
        progress,
        initials: readText(firstName).slice(0, 1).toUpperCase() || "S",
      };
    });
  }, [students, transactions, balance.totalCharges, balance.totalCredits]);

  const filteredTransactions = useMemo(
    () =>
      transactions.filter((entry) => {
        const kind = classifyTuitionTransaction(readText(entry.entryType, ""));
        if (transactionFilter === "all") return true;
        if (transactionFilter === "charges") return kind === "CHARGE";
        if (transactionFilter === "payments") return kind === "PAYMENT";
        return kind === "ADJUSTMENT";
      }),
    [transactions, transactionFilter],
  );

  const handleStatusChange = async (nextStatus: string) => {
    setStatusValue(nextStatus);
    await updateFamilyWorkflowStatus({
      input: {
        familyId,
        workflowStatus: nextStatus,
      },
    });
    await familyQuery.refetch();
  };

  const handleScheduleSubmit = async (draft: ScheduleEventDraft) => {
    if (draft.id) {
      await updateFamilyScheduleEvent({
        input: {
          eventId: draft.id,
          title: draft.title,
          date: draft.date,
          details: draft.details,
          authorName: "Staff",
        },
      });
    } else {
      await createFamilyScheduleEvent({
        input: {
          familyId,
          title: draft.title,
          date: draft.date,
          details: draft.details,
          authorName: "Staff",
        },
      });
    }

    await timelineQuery.refetch();
    setScheduleOpen(false);
    setScheduleDraft({ title: "Interview", date: "", details: "" });
  };

  const handleContactSubmit = async (draft: ContactDraft) => {
    await sendFamilyCommunication({
      input: {
        familyId,
        channel: "email",
        subject: draft.subject,
        body: draft.message,
        recipient: readText(primaryContact.email) || null,
        recipientName:
          `${readText(primaryContact.firstName)} ${readText(primaryContact.lastName)}`.trim(),
      },
    });
    await communicationsQuery.refetch();
    setContactOpen(false);
  };

  const handleSaveActiveTab = async () => {
    if (tab === "children") {
      const originalDrafts = buildStudentDrafts(students);
      const updateRequests = students.flatMap((student) => {
        const studentId = readText(student.id, "");
        if (!studentId) return [];

        const original = originalDrafts[studentId];
        const draft = studentDrafts[studentId] ?? original;
        if (!draft || !original) return [];

        const updates: Record<string, unknown> = {};
        const setIfChanged = (
          key: string,
          nextValue: unknown,
          previousValue: unknown,
        ) => {
          if (nextValue !== previousValue) {
            updates[key] = nextValue;
          }
        };

        const nextFirstName = normalizeRequiredDraftValue(
          draft.firstName,
          original.firstName,
        );
        const previousFirstName = normalizeRequiredDraftValue(
          original.firstName,
          original.firstName,
        );
        setIfChanged("firstName", nextFirstName, previousFirstName);

        const nextLastName = normalizeRequiredDraftValue(
          draft.lastName,
          original.lastName,
        );
        const previousLastName = normalizeRequiredDraftValue(
          original.lastName,
          original.lastName,
        );
        setIfChanged("lastName", nextLastName, previousLastName);

        const nextGrade = normalizeGradeForStorage(
          normalizeRequiredDraftValue(draft.grade, original.grade),
        );
        const previousGrade = normalizeGradeForStorage(
          normalizeRequiredDraftValue(original.grade, original.grade),
        );
        setIfChanged("grade", nextGrade, previousGrade);

        const nextDivision = normalizeRequiredDraftValue(
          draft.division,
          original.division,
        );
        const previousDivision = normalizeRequiredDraftValue(
          original.division,
          original.division,
        );
        setIfChanged("division", nextDivision, previousDivision);

        const nextStatus = normalizeRequiredDraftValue(
          draft.status,
          original.status,
        );
        const previousStatus = normalizeRequiredDraftValue(
          original.status,
          original.status,
        );
        setIfChanged("status", nextStatus, previousStatus);

        setIfChanged(
          "preferredName",
          normalizeNullableDraftValue(draft.preferredName),
          normalizeNullableDraftValue(original.preferredName),
        );
        setIfChanged(
          "gradeApplyingFor",
          normalizeGradeForStorage(draft.gradeApplyingFor) || null,
          normalizeGradeForStorage(original.gradeApplyingFor) || null,
        );
        setIfChanged(
          "applyingForYear",
          normalizeNullableDraftValue(draft.applyingForYear),
          normalizeNullableDraftValue(original.applyingForYear),
        );
        setIfChanged(
          "placeOfBirth",
          normalizeNullableDraftValue(draft.placeOfBirth),
          normalizeNullableDraftValue(original.placeOfBirth),
        );
        setIfChanged(
          "homeLanguage",
          normalizeNullableDraftValue(draft.homeLanguage),
          normalizeNullableDraftValue(original.homeLanguage),
        );
        setIfChanged(
          "currentSchool",
          normalizeNullableDraftValue(draft.currentSchool),
          normalizeNullableDraftValue(original.currentSchool),
        );
        setIfChanged(
          "principalName",
          normalizeNullableDraftValue(draft.principalName),
          normalizeNullableDraftValue(original.principalName),
        );
        setIfChanged(
          "principalPhone",
          normalizeNullableDraftValue(draft.principalPhone),
          normalizeNullableDraftValue(original.principalPhone),
        );
        setIfChanged(
          "pediatrician",
          normalizeNullableDraftValue(draft.pediatrician),
          normalizeNullableDraftValue(original.pediatrician),
        );
        setIfChanged(
          "pediatricianPhone",
          normalizeNullableDraftValue(draft.pediatricianPhone),
          normalizeNullableDraftValue(original.pediatricianPhone),
        );
        setIfChanged(
          "dentist",
          normalizeNullableDraftValue(draft.dentist),
          normalizeNullableDraftValue(original.dentist),
        );
        setIfChanged(
          "dentistPhone",
          normalizeNullableDraftValue(draft.dentistPhone),
          normalizeNullableDraftValue(original.dentistPhone),
        );

        if (Object.keys(updates).length === 0) {
          return [];
        }

        return [
          {
            input: {
              studentId,
              updates,
            },
          },
        ];
      });

      if (updateRequests.length > 0) {
        await Promise.all(
          updateRequests.map((request) => updateStudentProfile(request)),
        );
      }

      await familyQuery.refetch();
      setIsEditing(false);
      return;
    }

    if (tab === "family") {
      const originalFamily = buildFamilyDraft(familyRecord);
      const updates: Record<string, unknown> = {};
      const setIfChanged = (
        key: string,
        nextValue: unknown,
        previousValue: unknown,
      ) => {
        if (nextValue !== previousValue) {
          updates[key] = nextValue;
        }
      };

      const nextFamilyName = normalizeRequiredDraftValue(
        familyDraft.familyName,
        originalFamily.familyName,
      );
      const previousFamilyName = normalizeRequiredDraftValue(
        originalFamily.familyName,
        originalFamily.familyName,
      );
      setIfChanged("familyName", nextFamilyName, previousFamilyName);
      setIfChanged(
        "homePhone",
        normalizeNullableDraftValue(familyDraft.homePhone),
        normalizeNullableDraftValue(originalFamily.homePhone),
      );
      setIfChanged(
        "addressStreet",
        normalizeNullableDraftValue(familyDraft.addressStreet),
        normalizeNullableDraftValue(originalFamily.addressStreet),
      );
      setIfChanged(
        "addressCity",
        normalizeNullableDraftValue(familyDraft.addressCity),
        normalizeNullableDraftValue(originalFamily.addressCity),
      );
      setIfChanged(
        "addressState",
        normalizeNullableDraftValue(familyDraft.addressState),
        normalizeNullableDraftValue(originalFamily.addressState),
      );
      setIfChanged(
        "addressZip",
        normalizeNullableDraftValue(familyDraft.addressZip),
        normalizeNullableDraftValue(originalFamily.addressZip),
      );
      setIfChanged(
        "synagogue",
        normalizeNullableDraftValue(familyDraft.synagogue),
        normalizeNullableDraftValue(originalFamily.synagogue),
      );
      setIfChanged(
        "rabbi",
        normalizeNullableDraftValue(familyDraft.rabbi),
        normalizeNullableDraftValue(originalFamily.rabbi),
      );
      setIfChanged(
        "hebrewFamilyName",
        normalizeNullableDraftValue(familyDraft.hebrewFamilyName),
        normalizeNullableDraftValue(originalFamily.hebrewFamilyName),
      );
      setIfChanged(
        "maritalStatus",
        normalizeNullableDraftValue(familyDraft.maritalStatus),
        normalizeNullableDraftValue(originalFamily.maritalStatus),
      );
      setIfChanged(
        "adoptedChild",
        normalizeNullableDraftValue(familyDraft.adoptedChild),
        normalizeNullableDraftValue(originalFamily.adoptedChild),
      );
      setIfChanged(
        "stepParent",
        normalizeNullableDraftValue(familyDraft.stepParent),
        normalizeNullableDraftValue(originalFamily.stepParent),
      );

      const nextSiblings = normalizeSiblingDrafts(familyDraft.siblings);
      const previousSiblings = normalizeSiblingDrafts(originalFamily.siblings);
      if (JSON.stringify(nextSiblings) !== JSON.stringify(previousSiblings)) {
        updates.siblings = nextSiblings;
      }

      if (Object.keys(updates).length === 0) {
        setIsEditing(false);
        return;
      }

      await updateFamilyProfile({
        input: {
          familyId,
          updates,
        },
      });
      await familyQuery.refetch();
      setIsEditing(false);
    }
  };

  const handleAssignCaseOwner = async (
    studentId: string,
    caseOwnerName: string,
  ) => {
    setStudentDrafts((prev) => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        caseOwnerName,
      },
    }));

    await assignStudentCaseOwner({
      input: {
        studentId,
        caseOwnerName,
        caseOwnerId: caseOwnerName.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
      },
    });
  };

  const handleDocumentSubmit = async (payload: DocumentFormSubmission) => {
    if (payload.documentId) {
      await updateFamilyDocumentRecord({
        input: {
          documentId: payload.documentId,
          updates: {
            name: payload.name,
            category: payload.category,
            status: payload.status,
            notes: payload.notes || null,
            studentId: payload.selectedStudentIds[0],
            documentGroupId: payload.documentGroupId,
          },
        },
      });
      setDocumentModal({ open: false, mode: "create", document: null });
      await documentsQuery.refetch();
      return;
    }

    if (!payload.file) {
      throw new Error("A document file is required.");
    }

    const documentGroupId = createClientId();
    const uploadTicket = await requestFamilyDocumentUpload({
      input: {
        familyId,
        documentId: createClientId(),
        fileName: payload.file.name,
        fileType: payload.file.type,
      },
    });

    const ticket = asRecord(uploadTicket);
    const uploadUrl = readText(ticket.uploadUrl, "");
    const storageKey = readText(ticket.storageKey, "");
    const resourceId = readText(ticket.resourceId, storageKey);

    if (!uploadUrl || !storageKey) {
      throw new Error("Upload ticket was missing required fields.");
    }

    await uploadFileToSignedUrl(uploadUrl, payload.file);

    const studentIds =
      payload.selectedStudentIds.length > 0
        ? payload.selectedStudentIds
        : students.map((student) => readText(student.id)).filter(Boolean);

    await Promise.all(
      studentIds.map((studentId) =>
        createFamilyDocumentRecord({
          input: {
            familyId,
            studentId,
            documentGroupId,
            resourceId,
            name: payload.name,
            fileName: payload.file?.name,
            fileType: payload.file?.type,
            fileSizeBytes: payload.file?.size,
            storageKey,
            category: payload.category,
            status: payload.status,
            notes: payload.notes || null,
          },
        }),
      ),
    );

    setDocumentModal({ open: false, mode: "create", document: null });
    await documentsQuery.refetch();
  };

  const handleDeleteDocument = async (documentId: string) => {
    await deleteFamilyDocumentRecord({ input: { documentId } });
    await documentsQuery.refetch();
  };

  const handleDownloadDocument = async (documentId: string) => {
    const result = await runDownloadQuery("getFamilyDocumentDownloadUrl", {
      documentId,
    });
    const downloadUrl = readText(asRecord(result).downloadUrl, "");
    if (downloadUrl) {
      window.open(downloadUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleNoteAttachmentChange = (fileList: FileList | null) => {
    if (!fileList) return;
    const accepted: NoteAttachmentDraft[] = [];
    for (const file of Array.from(fileList)) {
      const validationError = validateUploadFile(file);
      if (validationError) {
        continue;
      }
      accepted.push({ id: createClientId(), file });
    }
    if (accepted.length > 0) {
      setNoteAttachments((prev) => [...prev, ...accepted]);
    }
  };

  const handleSaveNote = async () => {
    const trimmed = noteText.trim();
    if (!trimmed) return;

    const uploadedAttachments: Array<Record<string, unknown>> = [];

    for (const attachment of noteAttachments) {
      const uploadTicket = await requestFamilyNoteAttachmentUpload({
        input: {
          familyId,
          attachmentId: attachment.id,
          fileName: attachment.file.name,
          fileType: attachment.file.type,
        },
      });

      const ticket = asRecord(uploadTicket);
      const uploadUrl = readText(ticket.uploadUrl, "");
      const storageKey = readText(ticket.storageKey, "");
      const resourceId = readText(ticket.resourceId, storageKey);
      if (!uploadUrl || !storageKey) {
        throw new Error(
          "Note attachment upload ticket was missing required fields.",
        );
      }

      await uploadFileToSignedUrl(uploadUrl, attachment.file);
      uploadedAttachments.push({
        resourceId,
        storageKey,
        name: attachment.file.name,
        type: attachment.file.type,
        size: attachment.file.size,
      });
    }

    await createFamilyNote({
      input: {
        familyId,
        content: trimmed,
        category: noteTag,
        visibility: "internal",
        authorName: "Staff",
        attachments: uploadedAttachments,
      },
    });

    setNoteText("");
    setNoteTag("general");
    setNoteAttachments([]);
    await timelineQuery.refetch();
  };

  const handleDeleteNote = async (noteId: string) => {
    await deleteFamilyNote({ input: { noteId } });
    await timelineQuery.refetch();
  };

  const internalNoteItems = useMemo(
    () =>
      timelineItems
        .filter((entry) => {
          const eventType = readText(entry.eventType).toLowerCase();
          const title = readText(entry.title).toLowerCase();
          return eventType.includes("note") || title.includes("note");
        })
        .map((entry, index) => {
          const noteId = readText(entry.id, `family-note-${index}`);
          const content = readText(entry.content, "-");
          return {
            id: noteId,
            authorName: readText(
              entry.authorName,
              readText(entry.createdBy, "Staff"),
            ),
            createdAtLabel:
              formatTimelineDate(readText(entry.createdAt)) || "Recent",
            createdAtMs: Date.parse(readText(entry.createdAt, "")),
            content,
            tag: normalizeInternalNoteTag(
              entry.category ?? entry.noteType ?? entry.tag,
            ),
            onCopy: async () => {
              try {
                await navigator.clipboard.writeText(content);
              } catch {
                // no-op: copy feedback is non-blocking
              }
            },
            onDelete: () => handleDeleteNote(noteId),
          };
        })
        .sort((left, right) => {
          if (
            Number.isNaN(left.createdAtMs) ||
            Number.isNaN(right.createdAtMs)
          ) {
            return 0;
          }
          return right.createdAtMs - left.createdAtMs;
        })
        .map(({ createdAtMs: _createdAtMs, ...note }) => note),
    [timelineItems],
  );

  const handleAddPaymentMethod = async (draft: PaymentMethodDraft) => {
    await createFamilyPaymentMethod({
      input: {
        familyId,
        methodType: draft.methodType,
        label: draft.label,
        provider: draft.provider || null,
        lastFour: draft.lastFour || null,
        preferred: draft.preferred,
      },
    });

    setPaymentMethodOpen(false);
    await paymentMethodsQuery.refetch();
  };

  const handlePromotePaymentMethod = async (paymentMethodId: string) => {
    await updateFamilyPaymentMethod({
      input: {
        paymentMethodId,
        updates: {
          preferred: true,
          status: "active",
        },
      },
    });
    await paymentMethodsQuery.refetch();
  };

  const runAsyncAction = (action: () => Promise<unknown>) => {
    void action().catch(() => {
      // Request failures are surfaced via RTK mutation/query error state.
    });
  };

  const renderChildrenTab = () => (
    <div className="relative grid grid-cols-1 gap-4 pb-8 xl:grid-cols-2">
      {students.length === 0 ? (
        <p className="col-span-full rounded-2xl border border-gray-100 bg-white px-4 py-3 text-sm text-irongrey/50">
          No children are linked to this family yet.
        </p>
      ) : null}

      {students.map((student) => {
        const studentId = readText(student.id, createClientId());
        const draft =
          studentDrafts[studentId] ?? buildStudentDrafts([student])[studentId];
        const studentProfile = asRecord(student.profile);
        const fullName =
          `${draft.firstName} ${draft.lastName}`.trim() || "Student";
        const dateOfBirth = readText(
          studentProfile.dateOfBirth,
          readText(studentProfile.dob, "Not specified"),
        );
        const hebrewName = readText(studentProfile.hebrewName, "Not specified");
        const placeOfBirth = readText(
          draft.placeOfBirth,
          readText(studentProfile.placeOfBirth, "Not specified"),
        );
        const languageAtHome = readText(draft.homeLanguage, "Not specified");
        const conditions = readText(
          studentProfile.conditionsAndAllergies,
          readText(studentProfile.medicalConditions, "None reported"),
        );

        const setDraftValue = (key: keyof StudentDraft, value: string) => {
          setStudentDrafts((prev) => ({
            ...prev,
            [studentId]: {
              ...(prev[studentId] ?? draft),
              [key]: value,
            },
          }));
        };

        const schedulingRows = allTimelineRows
          .filter((row) => {
            const haystack = `${row.title} ${row.eventType}`.toLowerCase();
            return (
              row.source === "timeline" ||
              haystack.includes("schedule") ||
              haystack.includes("interview") ||
              haystack.includes("visit")
            );
          })
          .slice(0, 4);

        return (
          <article
            key={studentId}
            className="min-w-0 rounded-3xl border border-gray-100 bg-white p-4 shadow-card transition-shadow hover:shadow-soft sm:p-6"
          >
            <div className="mb-7 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex min-w-0 items-center gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-2xl text-primary sm:h-16 sm:w-16">
                  ◡
                </div>
                <div className="min-w-0">
                  <h3 className="truncate text-xl leading-7 font-medium tracking-tight text-irongrey sm:text-[24px] sm:leading-8">
                    {fullName}
                  </h3>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <StudentsFamiliesStatusBadge
                      status={draft.status || "active"}
                    />
                    <span className="text-sm font-medium text-irongrey/40">
                      ID: {studentId}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex w-full flex-wrap items-center justify-between gap-3 lg:w-auto lg:justify-end">
                <span className="text-sm font-bold uppercase tracking-widest text-irongrey/30">
                  {readText(draft.division, "division")}
                </span>
                <div className="relative w-full sm:w-auto">
                  <select
                    value={draft.caseOwnerName}
                    onChange={(event) => {
                      runAsyncAction(() =>
                        handleAssignCaseOwner(studentId, event.target.value),
                      );
                    }}
                    disabled={
                      !isEditing || assignStudentCaseOwnerState.isLoading
                    }
                    className="w-full appearance-none rounded-lg border border-primary/10 bg-primary/5 px-4 py-2 pr-8 text-xs font-semibold text-primary transition hover:bg-primary/10 disabled:cursor-not-allowed disabled:opacity-60 sm:max-w-[170px]"
                  >
                    {!draft.caseOwnerName ? (
                      <option value="">Assign staff</option>
                    ) : null}
                    {CASE_OWNER_OPTIONS.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={14}
                    className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-primary/60"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
              <div className="mt-1 border-t border-gray-100 pt-4 sm:col-span-2">
                <p className="text-[10px] font-bold uppercase tracking-widest text-irongrey/35">
                  This Application
                </p>
              </div>
              <div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                  Applying For
                </p>
                {isEditing ? (
                  <input
                    value={readText(draft.applyingForYear, "")}
                    onChange={(event) =>
                      setDraftValue("applyingForYear", event.target.value)
                    }
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-irongrey"
                  />
                ) : (
                  <p className="font-medium text-irongrey">
                    {readText(draft.applyingForYear, "Not specified")}
                  </p>
                )}
              </div>
              <div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                  Grade Applying For
                </p>
                {isEditing ? (
                  <input
                    value={formatGradeForDisplay(
                      draft.gradeApplyingFor || draft.grade,
                    )}
                    onChange={(event) =>
                      setDraftValue("gradeApplyingFor", event.target.value)
                    }
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-irongrey"
                  />
                ) : (
                  <p className="font-medium text-irongrey">
                    {formatGradeForDisplay(
                      draft.gradeApplyingFor || draft.grade,
                    ) || "Not specified"}
                  </p>
                )}
              </div>

              <div className="mt-2 border-t border-gray-100 pt-4 sm:col-span-2">
                <p className="text-[10px] font-bold uppercase tracking-widest text-irongrey/35">
                  Birth &amp; Identity
                </p>
              </div>
              <div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                  Name Child Is Called By
                </p>
                {isEditing ? (
                  <input
                    value={readText(draft.preferredName, "")}
                    onChange={(event) =>
                      setDraftValue("preferredName", event.target.value)
                    }
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-irongrey"
                  />
                ) : (
                  <p className="font-medium text-irongrey">
                    {readText(draft.preferredName, "Not specified")}
                  </p>
                )}
              </div>
              <div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                  Hebrew Name
                </p>
                <p className="font-medium text-irongrey">{hebrewName}</p>
              </div>
              <div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                  Date of Birth
                </p>
                <p className="font-medium text-irongrey">{dateOfBirth}</p>
              </div>
              <div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                  Place of Birth
                </p>
                {isEditing ? (
                  <input
                    value={readText(draft.placeOfBirth, "")}
                    onChange={(event) =>
                      setDraftValue("placeOfBirth", event.target.value)
                    }
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-irongrey"
                  />
                ) : (
                  <p className="font-medium text-irongrey">{placeOfBirth}</p>
                )}
              </div>
              <div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                  Language at Home
                </p>
                {isEditing ? (
                  <input
                    value={readText(draft.homeLanguage, "")}
                    onChange={(event) =>
                      setDraftValue("homeLanguage", event.target.value)
                    }
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-irongrey"
                  />
                ) : (
                  <p className="font-medium text-irongrey">{languageAtHome}</p>
                )}
              </div>
              <div />

              <div className="mt-2 border-t border-gray-100 pt-4 sm:col-span-2">
                <p className="text-[10px] font-bold uppercase tracking-widest text-irongrey/35">
                  Current Schooling
                </p>
              </div>
              <div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                  Current School
                </p>
                {isEditing ? (
                  <input
                    value={readText(draft.currentSchool, "")}
                    onChange={(event) =>
                      setDraftValue("currentSchool", event.target.value)
                    }
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-irongrey"
                  />
                ) : (
                  <p className="font-medium text-irongrey">
                    {readText(draft.currentSchool, "Not specified")}
                  </p>
                )}
              </div>
              <div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                  Current Grade
                </p>
                {isEditing ? (
                  <input
                    data-testid={`students-families-children-current-grade-input-${studentId}`}
                    value={formatGradeForDisplay(draft.grade)}
                    onChange={(event) =>
                      setDraftValue(
                        "grade",
                        normalizeGradeForStorage(event.target.value),
                      )
                    }
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-irongrey"
                  />
                ) : (
                  <p className="font-medium text-irongrey">
                    {formatGradeForDisplay(draft.grade) || "Not specified"}
                  </p>
                )}
              </div>
              <div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                  Director/Principal
                </p>
                {isEditing ? (
                  <input
                    value={readText(draft.principalName, "")}
                    onChange={(event) =>
                      setDraftValue("principalName", event.target.value)
                    }
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-irongrey"
                  />
                ) : (
                  <p className="font-medium text-irongrey">
                    {readText(draft.principalName, "Not specified")}
                  </p>
                )}
              </div>
              <div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                  Phone Number
                </p>
                {isEditing ? (
                  <input
                    value={readText(draft.principalPhone, "")}
                    onChange={(event) =>
                      setDraftValue("principalPhone", event.target.value)
                    }
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-irongrey"
                  />
                ) : (
                  <p className="font-medium text-irongrey">
                    {readText(draft.principalPhone, "Not specified")}
                  </p>
                )}
              </div>

              <div className="mt-2 border-t border-gray-100 pt-4 sm:col-span-2">
                <p className="text-[10px] font-bold uppercase tracking-widest text-irongrey/35">
                  Medical Contacts
                </p>
              </div>
              <div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                  Pediatrician
                </p>
                {isEditing ? (
                  <input
                    value={readText(draft.pediatrician, "")}
                    onChange={(event) =>
                      setDraftValue("pediatrician", event.target.value)
                    }
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-irongrey"
                  />
                ) : (
                  <p className="font-medium text-irongrey">
                    {readText(draft.pediatrician, "Not specified")}
                  </p>
                )}
              </div>
              <div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                  Phone
                </p>
                {isEditing ? (
                  <input
                    value={readText(draft.pediatricianPhone, "")}
                    onChange={(event) =>
                      setDraftValue("pediatricianPhone", event.target.value)
                    }
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-irongrey"
                  />
                ) : (
                  <p className="font-medium text-irongrey">
                    {readText(draft.pediatricianPhone, "Not specified")}
                  </p>
                )}
              </div>
              <div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                  Dentist
                </p>
                {isEditing ? (
                  <input
                    value={readText(draft.dentist, "")}
                    onChange={(event) =>
                      setDraftValue("dentist", event.target.value)
                    }
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-irongrey"
                  />
                ) : (
                  <p className="font-medium text-irongrey">
                    {readText(draft.dentist, "Not specified")}
                  </p>
                )}
              </div>
              <div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                  Phone
                </p>
                {isEditing ? (
                  <input
                    value={readText(draft.dentistPhone, "")}
                    onChange={(event) =>
                      setDraftValue("dentistPhone", event.target.value)
                    }
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-irongrey"
                  />
                ) : (
                  <p className="font-medium text-irongrey">
                    {readText(draft.dentistPhone, "Not specified")}
                  </p>
                )}
              </div>

              <div className="mt-2 border-t border-gray-100 pt-4 sm:col-span-2">
                <p className="text-[10px] font-bold uppercase tracking-widest text-irongrey/35">
                  Medical Conditions
                </p>
              </div>
              <div className="sm:col-span-2">
                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                  Conditions &amp; Allergies
                </p>
                <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm font-semibold text-irongrey/80">
                  {conditions}
                </div>
              </div>

              <div className="mt-2 border-t border-gray-100 pt-4 sm:col-span-2">
                <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-irongrey/35">
                  Scheduling
                </p>
                <div className="space-y-2 rounded-2xl border border-gray-100 bg-gray-50/70 p-3">
                  {schedulingRows.length > 0 ? (
                    schedulingRows.map((row) => {
                      const eventDate = new Date(readText(row.createdAt, ""));
                      const month = Number.isNaN(eventDate.getTime())
                        ? "NOW"
                        : eventDate
                            .toLocaleDateString("en-US", { month: "short" })
                            .toUpperCase();
                      const day = Number.isNaN(eventDate.getTime())
                        ? "--"
                        : eventDate.toLocaleDateString("en-US", {
                            day: "2-digit",
                          });
                      const stamp = [
                        formatTimelineDate(row.createdAt),
                        formatTimelineTime(row.createdAt),
                      ]
                        .filter(Boolean)
                        .join(" • ");
                      return (
                        <article
                          key={row.id}
                          className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white px-3 py-2"
                        >
                          <div className="w-11 rounded-lg border border-primary/10 bg-primary/5 px-1 py-1 text-center">
                            <p className="text-[8px] font-bold uppercase tracking-wide text-primary">
                              {month}
                            </p>
                            <p className="text-xs font-black text-irongrey">
                              {day}
                            </p>
                          </div>
                          <div className="min-w-0">
                            <p className="truncate text-xs font-bold text-irongrey">
                              {row.title}
                            </p>
                            <p className="text-[10px] font-medium text-irongrey/45">
                              {stamp || "Scheduled event"}
                            </p>
                          </div>
                        </article>
                      );
                    })
                  ) : (
                    <p className="rounded-xl border border-gray-100 bg-white px-3 py-2 text-xs italic text-irongrey/45">
                      No upcoming events scheduled for this child.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );

  const renderFamilyTab = () => {
    const homeAddressParts = [
      readText(familyDraft.addressStreet),
      readText(familyDraft.addressCity),
      readText(familyDraft.addressState),
      readText(familyDraft.addressZip),
    ].filter(Boolean);
    const homeAddress = homeAddressParts.join(", ");

    const pickValue = (
      source: JsonRecord,
      keys: string[],
      fallback = "Not specified",
    ): string => {
      for (const key of keys) {
        const value = readText(source[key], "");
        if (value) return value;
      }
      return fallback;
    };

    const father = asRecord(
      parentContacts.find((contact) =>
        /father|dad|male/i.test(
          readText(contact.relationship, readText(contact.type, "")),
        ),
      ) ?? parentContacts[0],
    );
    const mother = asRecord(
      parentContacts.find((contact) =>
        /mother|mom|female/i.test(
          readText(contact.relationship, readText(contact.type, "")),
        ),
      ) ?? parentContacts[1],
    );

    const mainContact = asRecord(primaryContact);
    const familyProfile = asRecord(familyRecord.profile);
    const grandparentRoot = asRecord(
      familyProfile.grandparents || familyRecord.grandparents,
    );
    const paternalGrandparents = asRecord(
      familyProfile.paternalGrandparents ||
        grandparentRoot.paternalGrandparents ||
        grandparentRoot.paternal ||
        familyRecord.paternalGrandparents,
    );
    const maternalGrandparents = asRecord(
      familyProfile.maternalGrandparents ||
        grandparentRoot.maternalGrandparents ||
        grandparentRoot.maternal ||
        familyRecord.maternalGrandparents,
    );

    const renderSiblingRows = () => (
      <div className="space-y-3">
        <div className="hidden grid-cols-12 gap-2 border-b border-gray-100 pb-2 md:grid">
          <div className="md:col-span-5 text-[10px] font-bold uppercase tracking-widest text-irongrey/35">
            Name
          </div>
          <div className="md:col-span-2 text-[10px] font-bold uppercase tracking-widest text-irongrey/35">
            Age
          </div>
          <div className="md:col-span-5 text-[10px] font-bold uppercase tracking-widest text-irongrey/35">
            School
          </div>
        </div>

        {familyDraft.siblings.length === 0 ? (
          <p className="text-sm italic text-irongrey/40">
            No other siblings listed
          </p>
        ) : (
          familyDraft.siblings.map((sibling, index) => (
            <div
              key={`sibling-${index}`}
              className="grid grid-cols-1 gap-2 rounded-2xl border border-gray-100 bg-gray-50/50 p-3 md:grid-cols-12 md:items-center md:rounded-none md:border-0 md:bg-transparent md:p-0"
            >
              {isEditing ? (
                <>
                  <input
                    value={sibling.name}
                    onChange={(event) => {
                      const next = [...familyDraft.siblings];
                      next[index] = {
                        ...next[index],
                        name: event.target.value,
                      };
                      setFamilyDraft((prev) => ({ ...prev, siblings: next }));
                    }}
                    placeholder="Name"
                    className="col-span-1 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs font-medium text-irongrey md:col-span-5"
                  />
                  <input
                    value={sibling.age}
                    onChange={(event) => {
                      const next = [...familyDraft.siblings];
                      next[index] = { ...next[index], age: event.target.value };
                      setFamilyDraft((prev) => ({ ...prev, siblings: next }));
                    }}
                    placeholder="Age"
                    className="col-span-1 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs font-medium text-irongrey md:col-span-2"
                  />
                  <input
                    value={sibling.school}
                    onChange={(event) => {
                      const next = [...familyDraft.siblings];
                      next[index] = {
                        ...next[index],
                        school: event.target.value,
                      };
                      setFamilyDraft((prev) => ({ ...prev, siblings: next }));
                    }}
                    placeholder="School"
                    className="col-span-1 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs font-medium text-irongrey md:col-span-5"
                  />
                </>
              ) : (
                <>
                  <div className="col-span-1 truncate text-sm font-semibold text-irongrey md:col-span-5">
                    {sibling.name || "-"}
                  </div>
                  <div className="col-span-1 text-sm font-medium text-irongrey/60 md:col-span-2">
                    {sibling.age || "-"}
                  </div>
                  <div className="col-span-1 truncate text-sm font-medium text-irongrey/60 md:col-span-5">
                    {sibling.school || "-"}
                  </div>
                </>
              )}
            </div>
          ))
        )}

        {isEditing ? (
          <button
            type="button"
            onClick={() => {
              setFamilyDraft((prev) => ({
                ...prev,
                siblings: [...prev.siblings, { name: "", age: "", school: "" }],
              }));
            }}
            className="w-full rounded-lg border border-primary/10 bg-primary/5 px-3 py-2 text-xs font-bold text-primary transition hover:bg-primary/10"
          >
            + Add Sibling
          </button>
        ) : null}
      </div>
    );

    return (
      <div className="flex flex-col gap-4 pb-8">
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <section className="rounded-2xl border border-gray-100 bg-white p-7 shadow-card transition-all hover:shadow-soft">
            <h3 className="mb-6 text-[24px] leading-8 font-medium tracking-tight text-irongrey">
              Home Contact
            </h3>
            <div className="space-y-4">
              <div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                  Family Name
                </p>
                {isEditing ? (
                  <input
                    data-testid="students-families-family-input-family-name"
                    value={readText(familyDraft.familyName)}
                    onChange={(event) =>
                      setFamilyDraft((prev) => ({
                        ...prev,
                        familyName: event.target.value,
                      }))
                    }
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-irongrey"
                  />
                ) : (
                  <p className="font-medium text-irongrey">
                    {readText(familyDraft.familyName, "Not specified")}
                  </p>
                )}
              </div>
              <div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                  Primary Contact
                </p>
                <p className="font-medium text-irongrey">
                  {readText(
                    `${readText(mainContact.firstName)} ${readText(mainContact.lastName)}`.trim(),
                    "Not specified",
                  )}
                </p>
              </div>
              <div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                  Email
                </p>
                <p className="font-medium text-irongrey">
                  {pickValue(mainContact, ["email", "contactEmail"])}
                </p>
              </div>
              <div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                  Phone
                </p>
                <p className="font-medium text-irongrey">
                  {pickValue(mainContact, [
                    "phone",
                    "cellPhone",
                    "mobilePhone",
                  ])}
                </p>
              </div>
              <div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                  Home Address
                </p>
                {isEditing ? (
                  <textarea
                    value={homeAddress}
                    onChange={(event) => {
                      const next = event.target.value;
                      setFamilyDraft((prev) => ({
                        ...prev,
                        addressStreet: next,
                        addressCity: "",
                        addressState: "",
                        addressZip: "",
                      }));
                    }}
                    className="w-full resize-none rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-irongrey"
                    rows={3}
                  />
                ) : (
                  <p className="font-medium text-irongrey">
                    {homeAddress || "Not specified"}
                  </p>
                )}
              </div>
              <div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                  Home Phone
                </p>
                {isEditing ? (
                  <input
                    data-testid="students-families-family-input-home-phone"
                    value={readText(familyDraft.homePhone)}
                    onChange={(event) =>
                      setFamilyDraft((prev) => ({
                        ...prev,
                        homePhone: event.target.value,
                      }))
                    }
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-irongrey"
                  />
                ) : (
                  <p className="font-medium text-irongrey">
                    {readText(familyDraft.homePhone, "Not specified")}
                  </p>
                )}
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-gray-100 bg-white p-7 shadow-card transition-all hover:shadow-soft">
            <h3 className="mb-6 text-[24px] leading-8 font-medium tracking-tight text-irongrey">
              Status &amp; Affiliation
            </h3>
            <div className="space-y-5">
              <div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                  Marital Status
                </p>
                {isEditing ? (
                  <input
                    data-testid="students-families-family-input-marital-status"
                    value={readText(familyDraft.maritalStatus)}
                    onChange={(event) =>
                      setFamilyDraft((prev) => ({
                        ...prev,
                        maritalStatus: event.target.value,
                      }))
                    }
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-irongrey"
                  />
                ) : (
                  <p className="font-medium text-irongrey">
                    {readText(familyDraft.maritalStatus, "Not specified")}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                    Adopted Child?
                  </p>
                  {isEditing ? (
                    <input
                      value={readText(familyDraft.adoptedChild)}
                      onChange={(event) =>
                        setFamilyDraft((prev) => ({
                          ...prev,
                          adoptedChild: event.target.value,
                        }))
                      }
                      className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-irongrey"
                    />
                  ) : (
                    <p className="font-medium text-irongrey">
                      {readText(familyDraft.adoptedChild, "Not specified")}
                    </p>
                  )}
                </div>
                <div>
                  <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                    Step-Parent?
                  </p>
                  {isEditing ? (
                    <input
                      value={readText(familyDraft.stepParent)}
                      onChange={(event) =>
                        setFamilyDraft((prev) => ({
                          ...prev,
                          stepParent: event.target.value,
                        }))
                      }
                      className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-irongrey"
                    />
                  ) : (
                    <p className="font-medium text-irongrey">
                      {readText(familyDraft.stepParent, "Not specified")}
                    </p>
                  )}
                </div>
              </div>
              <div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                  Current Shul
                </p>
                {isEditing ? (
                  <input
                    value={readText(familyDraft.synagogue)}
                    onChange={(event) =>
                      setFamilyDraft((prev) => ({
                        ...prev,
                        synagogue: event.target.value,
                      }))
                    }
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-irongrey"
                  />
                ) : (
                  <p className="font-medium text-irongrey">
                    {readText(familyDraft.synagogue, "Not specified")}
                  </p>
                )}
              </div>
              <div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                  RAV
                </p>
                {isEditing ? (
                  <input
                    value={readText(familyDraft.rabbi)}
                    onChange={(event) =>
                      setFamilyDraft((prev) => ({
                        ...prev,
                        rabbi: event.target.value,
                      }))
                    }
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-irongrey"
                  />
                ) : (
                  <p className="font-medium text-irongrey">
                    {readText(familyDraft.rabbi, "Not specified")}
                  </p>
                )}
              </div>
              <div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                  Hebrew Family Name
                </p>
                {isEditing ? (
                  <input
                    value={readText(familyDraft.hebrewFamilyName)}
                    onChange={(event) =>
                      setFamilyDraft((prev) => ({
                        ...prev,
                        hebrewFamilyName: event.target.value,
                      }))
                    }
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-irongrey"
                    dir="rtl"
                  />
                ) : (
                  <p className="font-medium text-irongrey" dir="rtl">
                    {readText(familyDraft.hebrewFamilyName, "Not specified")}
                  </p>
                )}
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-gray-100 bg-white p-7 shadow-card transition-all hover:shadow-soft">
            <h3 className="mb-6 text-[24px] leading-8 font-medium tracking-tight text-irongrey">
              Other Siblings
            </h3>
            {renderSiblingRows()}
          </section>
        </div>

        <section className="rounded-2xl border border-gray-100 bg-white p-7 shadow-card transition-all hover:shadow-soft">
          <h3 className="mb-6 text-[24px] leading-8 font-medium tracking-tight text-irongrey">
            Parents Information
          </h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {[
              {
                heading: "Father's Information",
                contact: father,
                fallback: "Father",
                includeMaiden: false,
              },
              {
                heading: "Mother's Information",
                contact: mother,
                fallback: "Mother",
                includeMaiden: true,
              },
            ].map((entry) => {
              const fullName = readText(
                `${readText(entry.contact.firstName)} ${readText(entry.contact.lastName)}`.trim(),
                pickValue(entry.contact, ["fullName", "name"], entry.fallback),
              );
              return (
                <article
                  key={entry.heading}
                  className="rounded-2xl border border-gray-100 bg-gray-50/50 p-6"
                >
                  <p className="border-b border-gray-200 pb-3 text-xs font-bold uppercase tracking-widest text-irongrey/50">
                    {entry.heading}
                  </p>
                  <div className="mt-4 grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                        Title
                      </p>
                      <p className="font-medium text-irongrey">
                        {pickValue(entry.contact, ["title", "prefix"])}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                        Full Name
                      </p>
                      <p className="font-medium text-irongrey">{fullName}</p>
                    </div>
                    {entry.includeMaiden ? (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                          Maiden Name
                        </p>
                        <p className="font-medium text-irongrey">
                          {pickValue(entry.contact, ["maidenName"])}
                        </p>
                      </div>
                    ) : (
                      <div />
                    )}
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                        Cell Phone
                      </p>
                      <p className="font-medium text-irongrey">
                        {pickValue(entry.contact, [
                          "phone",
                          "cellPhone",
                          "mobilePhone",
                        ])}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                        Occupation
                      </p>
                      <p className="font-medium text-irongrey">
                        {pickValue(entry.contact, ["occupation", "jobTitle"])}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                        Email
                      </p>
                      <p className="font-medium text-irongrey">
                        {pickValue(entry.contact, ["email"])}
                      </p>
                    </div>
                    <div className="sm:col-span-2">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                        Name of Firm
                      </p>
                      <p className="font-medium text-irongrey">
                        {pickValue(entry.contact, [
                          "firm",
                          "firmName",
                          "company",
                          "employer",
                          "nameOfFirm",
                        ])}
                      </p>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section className="rounded-2xl border border-gray-100 bg-white p-7 shadow-card transition-all hover:shadow-soft">
          <h3 className="mb-6 text-[24px] leading-8 font-medium tracking-tight text-irongrey">
            Grandparents Information
          </h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {[
              {
                heading: "Paternal Grandparents",
                source: paternalGrandparents,
                familyLabel: "Paternal",
              },
              {
                heading: "Maternal Grandparents",
                source: maternalGrandparents,
                familyLabel: "Maternal",
              },
            ].map((entry) => (
              <article
                key={entry.heading}
                className="rounded-2xl border border-gray-100 bg-gray-50/50 p-6"
              >
                <p className="border-b border-gray-200 pb-3 text-xs font-bold uppercase tracking-widest text-irongrey/50">
                  {entry.heading}
                </p>
                <div className="mt-4 space-y-4">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                      Names
                    </p>
                    <p className="font-medium text-irongrey">
                      {pickValue(entry.source, [
                        "names",
                        "name",
                        "grandparentsLastName",
                        "lastName",
                      ])}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                      Location
                    </p>
                    <p className="font-medium text-irongrey">
                      {pickValue(entry.source, [
                        "location",
                        "address",
                        "homeAddress",
                        "city",
                      ])}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                      Phone
                    </p>
                    <p className="font-medium text-irongrey">
                      {pickValue(entry.source, [
                        "phone",
                        "homePhone",
                        "cellPhone",
                      ])}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-irongrey/30">
                      Family
                    </p>
                    <p className="font-medium text-irongrey">
                      {pickValue(
                        entry.source,
                        ["family", "familyName", "relation"],
                        entry.familyLabel,
                      )}
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      </div>
    );
  };

  const renderTuitionTab = () => (
    <div className="flex flex-col gap-8 pb-12">
      <section
        data-testid="students-families-tuition-financial-overview"
        className="flex flex-col rounded-2xl border border-gray-100 bg-white p-7 shadow-card transition-all duration-150"
      >
        <div className="mb-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-lg font-bold tracking-tight text-irongrey">
            Financial Overview
          </h3>
          <span className="rounded-full border border-jungle/10 bg-jungle/5 px-4 py-1.5 text-[10px] font-medium uppercase tracking-[0.15em] text-jungle">
            {balanceDue <= 0 ? "Good Standing" : "Balance Due"}
          </span>
        </div>

        <div className="-mx-7 mb-8 h-px bg-gray-100" />

        <div className="grid grid-cols-1 gap-4 px-0 sm:grid-cols-2 sm:gap-8 sm:px-4 md:grid-cols-4">
          <article>
            <p className="mb-2.5 text-[10px] font-medium uppercase tracking-[0.2em] text-irongrey/30">
              Total Tuition
            </p>
            <p className="text-3xl font-medium tracking-tight text-irongrey">
              {formatCurrency(balance.totalCharges)}
            </p>
          </article>
          <article>
            <p className="mb-2.5 text-[10px] font-medium uppercase tracking-[0.2em] text-irongrey/30">
              Financial Aid
            </p>
            <p className="text-3xl font-medium tracking-tight text-jungle">
              -{formatCurrency(balance.totalCredits)}
            </p>
          </article>
          <article>
            <p className="mb-2.5 text-[10px] font-medium uppercase tracking-[0.2em] text-irongrey/30">
              Total Paid
            </p>
            <p className="text-3xl font-medium tracking-tight text-irongrey">
              {formatCurrency(readNumber(balance.totalCredits) * -1)}
            </p>
          </article>
          <article>
            <p className="mb-2.5 text-[10px] font-medium uppercase tracking-[0.2em] text-irongrey/30">
              Balance Due
            </p>
            <p className="text-3xl font-medium tracking-tight text-irongrey">
              {formatCurrency(balanceDue)}
            </p>
          </article>
        </div>
      </section>

      <section data-testid="students-families-tuition-student-billing-section">
        <div className="mb-4 flex flex-col items-start gap-2 px-2 sm:flex-row sm:items-center sm:justify-between">
          <h4 className="text-[10px] font-medium uppercase tracking-[0.2em] text-irongrey/30">
            Student Billing Breakdown
          </h4>
        </div>

        {studentBillingRows.length === 0 ? (
          <div className="rounded-2xl border border-gray-100 bg-white p-6 text-sm text-irongrey/50 shadow-card">
            Student billing breakdown is not configured for this family yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {studentBillingRows.map((student) => (
              <article
                key={student.id}
                data-testid={`students-families-tuition-student-card-${student.id}`}
                className="rounded-2xl border border-gray-100 bg-white p-7 shadow-card transition-all hover:shadow-soft"
              >
                <div className="mb-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-xl font-medium text-white shadow-lg shadow-primary/20">
                      {student.initials}
                    </div>
                    <div className="min-w-0">
                      <h5 className="truncate text-xl font-medium text-irongrey">
                        {student.displayName}
                      </h5>
                      <p className="text-[10px] font-bold tracking-widest text-irongrey/40">
                        {student.gradeLabel}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="mb-1 text-[9px] font-medium uppercase tracking-widest text-primary">
                      Balance
                    </p>
                    <p className="text-2xl font-medium text-irongrey">
                      {formatCurrency(student.balance)}
                    </p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-[10px] font-medium uppercase tracking-widest text-irongrey/40">
                        Payment Progress
                      </span>
                      <span className="text-[10px] font-medium uppercase tracking-widest text-primary">
                        {student.progress}% Complete
                      </span>
                    </div>
                    <div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-100 p-0.5">
                      <div
                        className="h-full rounded-full bg-primary shadow-sm shadow-primary/20 transition-all duration-700 ease-out"
                        style={{ width: `${student.progress}%` }}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-4">
                      <p className="mb-1.5 text-[9px] font-medium uppercase tracking-widest text-irongrey/40">
                        Total Tuition
                      </p>
                      <p className="text-sm font-medium text-irongrey">
                        {formatCurrency(student.tuition)}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-4">
                      <p className="mb-1.5 text-[9px] font-medium uppercase tracking-widest text-irongrey/40">
                        Amount Paid
                      </p>
                      <p className="text-sm font-medium text-jungle">
                        {formatCurrency(student.paid)}
                      </p>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section data-testid="students-families-tuition-payment-methods">
        <div className="mb-4 flex flex-col items-start gap-2 px-2 sm:flex-row sm:items-center sm:justify-between">
          <h4 className="text-[10px] font-medium uppercase tracking-[0.2em] text-irongrey/30">
            Payment Methods
          </h4>
        </div>

        <div className="grid grid-cols-1 gap-3">
          {paymentMethodItems.map((method) => {
            const methodId = readText(method.id, "");
            const isCard =
              readText(method.methodType).toLowerCase() !== "bank_account";
            return (
              <article
                key={methodId}
                className="group flex flex-col items-start gap-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-card transition-all hover:border-gray-300 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex min-w-0 items-center gap-5">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/5 text-primary transition-colors group-hover:bg-primary/10">
                    {isCard ? <CreditCard size={22} /> : <Landmark size={22} />}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-irongrey">
                      {readText(method.label, "Payment Method")}
                    </p>
                    <p className="truncate text-xs text-irongrey/50">
                      {readText(method.provider)}
                      {readText(method.lastFour)
                        ? ` •••• ${readText(method.lastFour)}`
                        : ""}
                    </p>
                    {Boolean(method.preferred) ? (
                      <span className="mt-1 inline-block rounded-full border border-jungle/10 bg-jungle/5 px-2 py-0.5 text-[9px] font-medium uppercase tracking-widest text-jungle">
                        Preferred Source
                      </span>
                    ) : null}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    if (!methodId) return;
                    runAsyncAction(() => handlePromotePaymentMethod(methodId));
                  }}
                  className="p-2 text-irongrey/30 transition-colors hover:text-irongrey"
                  title="Set preferred"
                >
                  <Settings size={18} />
                </button>
              </article>
            );
          })}

          <button
            type="button"
            data-testid="students-families-tuition-add-payment-method-button"
            onClick={() => setPaymentMethodOpen(true)}
            className="group flex w-full items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-gray-100 py-5 text-[10px] font-medium uppercase tracking-[0.2em] text-irongrey/40 transition-all hover:border-gray-200 hover:bg-gray-50"
          >
            <Plus
              size={16}
              className="transition-transform group-hover:scale-110"
            />
            Add Secure Payment Method
          </button>
        </div>
      </section>

      <section
        data-testid="students-families-tuition-transaction-ledger"
        className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-card transition-all duration-150"
      >
        <div className="flex flex-col gap-4 border-b border-gray-50 bg-gray-50/30 px-7 py-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Receipt size={20} className="text-irongrey/30" />
            <h4 className="text-lg font-medium tracking-tight text-irongrey">
              Transaction Ledger
            </h4>
          </div>
          <div className="relative">
            <select
              data-testid="students-families-tuition-transaction-filter"
              value={transactionFilter}
              onChange={(event) =>
                setTransactionFilter(
                  event.target.value as
                    | "all"
                    | "charges"
                    | "payments"
                    | "adjustments",
                )
              }
              className="appearance-none rounded-xl border border-gray-200 bg-white px-4 py-2 pr-9 text-xs font-bold text-irongrey shadow-sm"
            >
              <option value="all">All Transactions</option>
              <option value="payments">Payments</option>
              <option value="charges">Charges</option>
              <option value="adjustments">Adjustments</option>
            </select>
            <ChevronDown
              size={14}
              className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-irongrey/40"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table
            data-testid="students-families-tuition-transaction-table"
            className="w-full border-collapse text-left"
          >
            <thead>
              <tr className="border-b border-gray-50/50">
                <th className="px-7 py-4 text-[10px] font-medium uppercase tracking-[0.2em] text-irongrey/30">
                  Date
                </th>
                <th className="px-7 py-4 text-[10px] font-medium uppercase tracking-[0.2em] text-irongrey/30">
                  Description
                </th>
                <th className="px-7 py-4 text-[10px] font-medium uppercase tracking-[0.2em] text-irongrey/30">
                  Type
                </th>
                <th className="px-7 py-4 text-right text-[10px] font-medium uppercase tracking-[0.2em] text-irongrey/30">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredTransactions.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-7 py-6 text-sm text-irongrey/50"
                  >
                    {transactions.length === 0
                      ? "Transactions have not been recorded yet."
                      : "No transactions match the selected filter."}
                  </td>
                </tr>
              ) : null}

              {filteredTransactions.map((entry, index) => {
                const entryId = readText(entry.id, `tx-${index}`);
                const amount = readNumber(entry.amount, 0);
                const entryType = readText(entry.entryType, "entry");
                const kind = classifyTuitionTransaction(entryType);
                return (
                  <tr
                    key={entryId}
                    className="group transition-colors hover:bg-gray-50/50"
                  >
                    <td className="px-7 py-5 text-sm font-medium tracking-tighter text-irongrey/40">
                      {formatTimelineDate(entry.effectiveDate)}
                    </td>
                    <td className="px-7 py-5">
                      <span className="text-sm font-bold text-irongrey">
                        {readText(entry.description, "-")}
                      </span>
                    </td>
                    <td className="px-7 py-5">
                      <span
                        className={`rounded-lg border px-2 py-1 text-[9px] font-medium uppercase tracking-widest ${
                          kind === "CHARGE"
                            ? "border-gray-100 bg-gray-50 text-irongrey"
                            : kind === "PAYMENT"
                              ? "border-jungle/10 bg-jungle/5 text-jungle"
                              : "border-primary/10 bg-primary/5 text-primary"
                        }`}
                      >
                        {kind}
                      </span>
                    </td>
                    <td
                      className={`px-7 py-5 text-right text-sm font-medium ${amount < 0 ? "text-jungle" : "text-irongrey"}`}
                    >
                      {amount < 0 ? "-" : ""}
                      {formatCurrency(Math.abs(amount))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="bg-gray-50/30 p-8 text-center">
          <button
            type="button"
            onClick={() => {
              if (transactionsNextToken) {
                setTransactionCursorArg(transactionsNextToken);
              }
            }}
            disabled={!transactionsNextToken || transactionsQuery.isLoading}
            className="text-[10px] font-medium uppercase tracking-[0.2em] text-primary transition-all hover:text-primary/80 disabled:cursor-not-allowed disabled:text-gray-400"
          >
            {transactionsQuery.isLoading
              ? "Loading..."
              : "Load More Transactions"}
          </button>
        </div>
      </section>
    </div>
  );

  const renderDocumentsTab = () => (
    <div className="flex flex-col gap-6 pb-20">
      <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full max-w-none sm:max-w-md">
          <Search
            size={18}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-irongrey/30"
          />
          <input
            value={documentsSearch}
            onChange={(event) => setDocumentsSearch(event.target.value)}
            placeholder="Search documents..."
            className="w-full rounded-2xl border border-gray-100 bg-white py-2.5 pl-10 pr-4 text-sm font-medium text-irongrey shadow-sm"
          />
        </div>

        <button
          type="button"
          data-testid="students-families-documents-add-button"
          onClick={() =>
            setDocumentModal({ open: true, mode: "create", document: null })
          }
          aria-label="Add Document"
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-white shadow-lg shadow-primary/20 transition-all hover:bg-primary/90"
        >
          <Plus size={18} />
        </button>
      </div>

      <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-card">
        <div className="overflow-x-auto">
          <table className="table-sticky-last-action-col-md-up min-w-full border-collapse text-left">
            <thead>
              <tr className="border-b border-gray-50 bg-gray-50/30">
                <th className="px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
                  Document Name
                </th>
                <th className="px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
                  Student
                </th>
                <th className="px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
                  Category
                </th>
                <th className="px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
                  Date
                </th>
                <th className="px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
                  Status
                </th>
                <th className="px-6 py-4 text-right text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredDocuments.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-6 text-sm text-irongrey/50"
                  >
                    No documents found for this family.
                  </td>
                </tr>
              ) : null}

              {filteredDocuments.map((doc, index) => {
                const documentId = readText(doc.id, `doc-${index}`);
                return (
                  <tr
                    key={documentId}
                    className="group cursor-pointer transition-colors hover:bg-gray-50/50"
                  >
                    <td className="px-6 py-4">
                      <span className="text-sm font-bold text-irongrey">
                        {readText(doc.name, "Document")}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-irongrey/70">
                      {readText(doc.studentId, "-")}
                    </td>
                    <td className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-irongrey/40">
                      {readText(doc.category, "-")}
                    </td>
                    <td className="px-6 py-4 text-xs font-medium text-irongrey/50">
                      {formatTimelineDate(doc.uploadedAt)}
                    </td>
                    <td className="px-6 py-4">
                      <StudentsFamiliesStatusBadge
                        status={normalizeStatusLabel(
                          readText(doc.status, "pending"),
                        )}
                      />
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="inline-flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                        <button
                          type="button"
                          onClick={() => {
                            runAsyncAction(() =>
                              handleDownloadDocument(documentId),
                            );
                          }}
                          className="rounded-lg p-2 text-irongrey/40 hover:bg-gray-100 hover:text-primary"
                          title="Download"
                        >
                          <Download size={15} />
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setDocumentModal({
                              open: true,
                              mode: "edit",
                              document: doc,
                            })
                          }
                          className="rounded-lg p-2 text-irongrey/40 hover:bg-gray-100 hover:text-primary"
                          title="Edit"
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            runAsyncAction(() =>
                              handleDeleteDocument(documentId),
                            );
                          }}
                          className="rounded-lg p-2 text-irongrey/40 hover:bg-gray-100 hover:text-strawberry"
                          title="Delete"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col items-start gap-3 border-t border-gray-50 bg-gray-50/30 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-medium text-irongrey/40">
            Showing {filteredDocuments.length} documents
          </p>
          <div className="inline-flex items-center gap-2">
            <button
              type="button"
              className="rounded-lg border border-gray-200 p-2 text-irongrey/30"
              aria-label="Previous documents page"
            >
              &lt;
            </button>
            <button
              type="button"
              className="rounded-lg border border-gray-200 p-2 text-irongrey/30"
              aria-label="Next documents page"
            >
              &gt;
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderNotesTab = () => (
    <div className="grid h-full grid-cols-1 gap-6 overflow-hidden bg-background-light pb-10 xl:grid-cols-2 xl:gap-0">
      <input
        ref={noteAttachmentInputRef}
        type="file"
        data-testid="students-families-notes-attachment-input"
        className="hidden"
        multiple
        onChange={(event) => {
          handleNoteAttachmentChange(event.target.files);
          event.currentTarget.value = "";
        }}
      />

      <section className="w-full overflow-y-auto xl:border-r xl:border-gray-100 xl:pr-8">
        <div className="pt-4">
          <div className="mb-6 flex items-center justify-between">
            <h3 className="text-xl font-bold text-irongrey">Internal Notes</h3>
          </div>

          <InternalNotesComposerCard
            noteText={noteText}
            onNoteTextChange={setNoteText}
            noteTag={noteTag}
            onNoteTagChange={setNoteTag}
            placeholder="Start typing a private note about this family..."
            currentDateLabel={new Date().toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
            onSave={async () => {
              await runAsyncAction(handleSaveNote);
            }}
            isSaving={
              createFamilyNoteState.isLoading ||
              requestFamilyNoteAttachmentUploadState.isLoading
            }
            isDisabled={
              createFamilyNoteState.isLoading ||
              requestFamilyNoteAttachmentUploadState.isLoading
            }
            attachmentButton={{
              dataTestId: "students-families-notes-attachment-button",
              onClick: () => noteAttachmentInputRef.current?.click(),
            }}
            attachments={noteAttachments.map((attachment, index) => ({
              id: attachment.id,
              label: truncateAttachmentLabel(attachment.file.name),
              dataTestId: "students-families-notes-attachment-row",
              onRemove: () =>
                setNoteAttachments((prev) =>
                  prev.filter((_, idx) => idx !== index),
                ),
            }))}
          />

          <InternalNotesListSection
            notes={internalNoteItems}
            emptyMessage="No internal notes yet."
          />
        </div>
      </section>

      <section className="w-full overflow-y-auto xl:pl-8">
        <div className="pt-4">
          <h3 className="mb-8 text-xl font-bold text-irongrey">
            Activity Timeline
          </h3>
          {allTimelineRows.length === 0 ? (
            <p className="rounded-2xl border border-gray-100 bg-white px-4 py-3 text-sm text-irongrey/50">
              No timeline activity yet.
            </p>
          ) : null}

          <div className="relative ml-4">
            <div className="absolute bottom-0 left-0 top-0 w-0.5 bg-gray-200" />
            <div className="relative space-y-10 py-1">
              {allTimelineRows.map((row) => {
                const isSchedule = row.eventType.toLowerCase() === "schedule";
                const isTimelineEntry = row.source === "timeline";
                return (
                  <article key={row.id} className="relative pl-10">
                    <div className="absolute left-[-5.5px] top-1.5 h-3 w-3 rounded-full bg-primary ring-4 ring-background-light shadow-sm" />
                    <div className="flex flex-col gap-1.5">
                      <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
                          {formatTimelineDate(row.createdAt)}
                        </p>
                        <div className="flex items-center gap-2">
                          {isSchedule && isTimelineEntry ? (
                            <button
                              type="button"
                              onClick={() => {
                                const content = readText(row.raw.content);
                                const inferredDate =
                                  /\d{4}-\d{2}-\d{2}/.exec(content)?.[0] ?? "";
                                setScheduleDraft({
                                  id: row.id,
                                  title: row.title,
                                  date: inferredDate,
                                  details: content,
                                });
                                setScheduleOpen(true);
                              }}
                              className="flex items-center gap-1 rounded-lg border border-primary/10 bg-primary/5 px-2 py-0.5 text-[10px] font-bold text-primary transition-colors hover:bg-primary/10"
                            >
                              Modify
                            </button>
                          ) : null}
                          {isTimelineEntry &&
                          row.eventType.toLowerCase() === "note" ? (
                            <button
                              type="button"
                              onClick={() => {
                                runAsyncAction(() => handleDeleteNote(row.id));
                              }}
                              className="flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10px] font-bold text-rose-600 transition-colors hover:bg-rose-100"
                            >
                              Delete
                            </button>
                          ) : null}
                        </div>
                      </div>

                      <p className="text-sm font-bold leading-tight text-irongrey">
                        {row.title}
                      </p>
                      <p className="whitespace-pre-wrap text-sm leading-relaxed text-irongrey/60">
                        {row.description}
                      </p>
                      <p className="text-[11px] font-medium uppercase text-irongrey/30">
                        {formatTimelineTime(row.createdAt)}
                      </p>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </div>
  );

  const renderTab = () => {
    if (tab === "children") return renderChildrenTab();
    if (tab === "family") return renderFamilyTab();
    if (tab === "tuition") return renderTuitionTab();
    if (tab === "documents") return renderDocumentsTab();
    return renderNotesTab();
  };

  return (
    <div
      ref={detailRootRef}
      data-testid="students-families-family-detail-root"
      data-layout-mode={layoutMode}
      className="bg-slate-50 px-4 py-4 md:px-6 md:py-6"
    >
      <div className="flex min-h-full flex-col">
        <DetailSectionHeader
          back={{
            kind: "button",
            onClick: () => navigate("/school/students/dashboard"),
          }}
          items={sectionNavItems}
          activeId={tab}
          ariaLabel="Family detail tabs"
          compact={shouldCollapseTabs}
          isCompactLayout={isCompactLayout}
          showBackText={showBackText}
          className={joinClassNames(
            "mt-4 mb-4 flex flex-shrink-0 justify-between border-b border-gray-200",
            shouldCollapseTabs ? "flex-wrap items-start gap-3" : "items-center",
          )}
          leadingClassName={headerLeadingClass}
          desktopBordered={false}
          actionsClassName={actionRowClass}
          desktopClassName="gap-3 md:gap-4"
          triggerClassName="w-full min-w-0"
          testIds={{
            root: "students-families-detail-tab-nav",
            itemPrefix: "students-families-tab",
            mobileTrigger: "students-families-mobile-tab-select",
            mobileDialog: "students-families-mobile-tab-menu",
            mobileItemPrefix: "students-families-mobile-tab",
          }}
          actions={
            <>
              <ActionIconButton
                dataTestId="students-families-family-schedule-button"
                icon={CalendarPlus}
                label="Schedule"
                showLabel={showPrimaryActionText}
                compact={shouldCollapseTabs}
                onClick={() => {
                  setScheduleDraft({
                    title: "Interview",
                    date: "",
                    details: "",
                  });
                  setScheduleOpen(true);
                }}
              />

              <ActionIconButton
                dataTestId="students-families-family-contact-button"
                icon={MailPlus}
                label="Contact Family"
                showLabel={showPrimaryActionText}
                compact={shouldCollapseTabs}
                onClick={() => setContactOpen(true)}
              />

              <InlineSelectControl
                dataTestId="students-families-family-status-select"
                value={statusValue}
                options={STATUS_OPTIONS}
                compact={shouldCollapseTabs}
                onChange={(nextStatus) => {
                  runAsyncAction(() => handleStatusChange(nextStatus));
                }}
              />

              {primaryHeaderAction === "edit" ? (
                <ActionIconButton
                  dataTestId="students-families-family-edit-button"
                  icon={Edit2}
                  label="Edit"
                  showLabel={showPrimaryActionText}
                  compact={shouldCollapseTabs}
                  disabled={headerBusy || !familyDetailHydrated}
                  onClick={() => {
                    if (!familyDetailHydrated) return;
                    setIsEditing(true);
                  }}
                />
              ) : null}

              {primaryHeaderAction === "save" ? (
                <ActionIconButton
                  dataTestId="students-families-family-save-button"
                  icon={Save}
                  label="Save"
                  showLabel={showPrimaryActionText}
                  compact={shouldCollapseTabs}
                  compactPaddingClassName="px-2.5"
                  variant="primary"
                  disabled={!isEditing || headerBusy}
                  onClick={() => {
                    runAsyncAction(handleSaveActiveTab);
                  }}
                  className={isCompactLayout ? "" : "px-4"}
                />
              ) : null}
            </>
          }
        />

        {renderTab()}
      </div>

      {familyQuery.isLoading ||
      balanceQuery.isLoading ||
      transactionsQuery.isLoading ||
      timelineQuery.isLoading ? (
        <p className="rounded-xl border border-gray-100 bg-white px-4 py-3 text-sm text-irongrey/50">
          Loading family detail...
        </p>
      ) : null}

      {hasRouterError ? (
        <SupportErrorMessage
          data-testid="students-families-domain-router-error-banner"
        >
          A domain-router request failed. Please retry.
          {isDevRuntimeBuild() && saveMutationErrorMessage ? (
            <span className="mt-1 block text-sm font-medium text-rose-800">
              {saveMutationErrorMessage}
            </span>
          ) : null}
        </SupportErrorMessage>
      ) : null}

      <ContactFamilyModal
        open={contactOpen}
        initialDraft={{ subject: "", message: "" }}
        onClose={() => setContactOpen(false)}
        onSubmit={handleContactSubmit}
        loading={sendFamilyCommunicationState.isLoading}
      />

      <ScheduleModal
        open={scheduleOpen}
        initial={scheduleDraft}
        onClose={() => {
          setScheduleOpen(false);
          setScheduleDraft({ title: "Interview", date: "", details: "" });
        }}
        onSubmit={handleScheduleSubmit}
        loading={
          createFamilyScheduleEventState.isLoading ||
          updateFamilyScheduleEventState.isLoading
        }
      />

      <DocumentModal
        open={documentModal.open}
        students={students}
        initialDocument={documentModal.document}
        onClose={() =>
          setDocumentModal({ open: false, mode: "create", document: null })
        }
        onSubmit={handleDocumentSubmit}
        loading={
          requestFamilyDocumentUploadState.isLoading ||
          createFamilyDocumentRecordState.isLoading ||
          updateFamilyDocumentRecordState.isLoading
        }
      />

      <PaymentMethodModal
        open={paymentMethodOpen}
        onClose={() => setPaymentMethodOpen(false)}
        onSubmit={handleAddPaymentMethod}
        loading={createFamilyPaymentMethodState.isLoading}
      />
    </div>
  );
};

export const ChildrenTabSurface = () => <FamilyDetail forcedTab="children" />;
export const FamilyTabSurface = () => <FamilyDetail forcedTab="family" />;
export const TuitionTabSurface = () => <FamilyDetail forcedTab="tuition" />;
export const DocumentsTabSurface = () => <FamilyDetail forcedTab="documents" />;
export const NotesTabSurface = () => <FamilyDetail forcedTab="notes" />;

export const FamilyChildrenPage = () => <FamilyDetail />;
export const FamilyFamilyPage = () => <FamilyDetail />;
export const FamilyTuitionPage = () => <FamilyDetail />;
export const FamilyDocumentsPage = () => <FamilyDetail />;
export const FamilyNotesPage = () => <FamilyDetail />;
